import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, apiFetchJson } from "../api";
import StyledSelect from "./StyledSelect";
import { SatisfactionBadge } from "./AccountTile";
import {
  XIcon,
  ChevronRightIcon,
  LoadingIcon,
  FileIcon,
  SendIcon,
  SearchIcon,
  SparklesIcon,
  StarIcon,
  CheckIcon,
  UserIcon,
  LinkIcon,
  CropIcon,
  UploadIcon,
  PlusIcon,
  MapPinIcon,
  EyeIcon
} from "./Icons";
import {
  Camera,
  Package as PackageGlyph,
  Smartphone,
  Laptop,
  Monitor,
  Tablet,
  Headphones,
  Gamepad2,
  Tv,
  Utensils,
  Watch,
  Bike,
  Sailboat,
  Car,
  Sofa,
  Flower2,
  Hammer,
  Lamp,
  Home,
  Footprints,
  Shirt,
  Baby,
  BookOpen,
  Music,
  PawPrint,
  Ticket,
  Dumbbell,
  Tent,
  Gem,
  Puzzle,
  Briefcase,
  GraduationCap,
  Wrench,
  HeartHandshake,
  Gift
} from "lucide-react";
import PhotoEditor from "./PhotoEditor";
import AdPreviewDialog from "./AdPreviewDialog";
import { t, tf, locale } from "../i18n";
import { catText, useCategoryLang } from "../categoryLang";
import { useAiPrefs } from "../utils/aiPrefs";
import { lockBodyScroll, unlockBodyScroll, useModalTouchGuard } from "../utils/bodyScrollLock";
import { useModalPresence, overlayMotionProps, dialogMotionProps } from "../utils/modalPresence";
import { useEscapeLayer } from "../utils/escapeLayers";
import { aa, S } from "../theme/palette";
import { buildPhotoSequence, moveInSequence } from "../utils/photoOrder";

const AD_MODAL_DEBUG = false;
const adModalDebugLog = (...args) => {
  if (!AD_MODAL_DEBUG) return;
  console.log(...args);
};
const adModalDebugWarn = (...args) => {
  if (!AD_MODAL_DEBUG) return;
  console.warn(...args);
};
const adModalDebugError = (...args) => {
  if (!AD_MODAL_DEBUG) return;
  console.error(...args);
};
const EBAY_IMPORT_MAX_IMAGES = 20;
// Сколько фото в объявлении всего — вместе с фото черновика (счётчик «из 20»).
const AD_MAX_IMAGES = 20;
const EBAY_ITEM_URL_PATTERN = /^https?:\/\/(?:www\.)?ebay\.[^/]+\/.+/i;

// Площадки граббера. eBay остаётся на своём маршруте — у него отдельный разбор,
// выросший вокруг его же особенностей; остальные идут общим.
const GRAB_SITES = [
  { id: "ebay", label: "eBay", pattern: /(^|\.)ebay\.[a-z.]+$/i, hint: "ebay.de/itm/..." },
  { id: "kleinanzeigen", label: "Kleinanzeigen", pattern: /(^|\.)kleinanzeigen\.de$/i, hint: "kleinanzeigen.de/s-anzeige/..." },
  { id: "willhaben", label: "willhaben", pattern: /(^|\.)willhaben\.at$/i, hint: "willhaben.at/iad/..." },
  { id: "marktplaats", label: "Marktplaats", pattern: /(^|\.)marktplaats\.nl$/i, hint: "marktplaats.nl/v/..." },
  { id: "ricardo", label: "Ricardo", pattern: /(^|\.)ricardo\.ch$/i, hint: "ricardo.ch/de/a/..." },
  { id: "picclick", label: "PicClick", pattern: /(^|\.)picclick\.[a-z.]+$/i, hint: "picclick.de/...-123456789.html" }
];

const detectGrabSiteId = (value) => {
  let host = "";
  try { host = new URL(String(value || "").trim()).hostname; } catch (_) { return ""; }
  return (GRAB_SITES.find((site) => site.pattern.test(host)) || {}).id || "";
};
const PUBLISH_IMAGE_ALLOWED_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/gif"]);
const PUBLISH_IMAGE_ALLOWED_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif"]);

const looksLikeEbayUrl = (value) => EBAY_ITEM_URL_PATTERN.test(String(value || "").trim());

const detectImageExtension = (contentType = "", url = "") => {
  const mime = String(contentType || "").toLowerCase();
  if (mime.includes("jpeg") || mime.includes("jpg")) return ".jpg";
  if (mime.includes("png")) return ".png";
  if (mime.includes("webp")) return ".webp";
  if (mime.includes("gif")) return ".gif";
  if (mime.includes("avif")) return ".avif";
  const cleanUrl = String(url || "").split("?")[0];
  const extMatch = cleanUrl.match(/\.(jpg|jpeg|png|webp|gif|avif)$/i);
  if (!extMatch) return ".jpg";
  const ext = String(extMatch[1] || "").toLowerCase();
  return ext === "jpeg" ? ".jpg" : `.${ext}`;
};

const normalizeEbayImportImageIdentity = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    parsed.hash = "";
    const normalizedPath = String(parsed.pathname || "")
      .replace(/\/thumbs\/images\//i, "/images/")
      .replace(/s-l\d+(?:\.[a-z0-9]+)?$/i, "s-lx")
      .replace(/\.(?:jpe?g|png|webp|gif|bmp|avif)$/i, "");
    return `${String(parsed.hostname || "").toLowerCase()}${normalizedPath}`;
  } catch (error) {
    return raw;
  }
};

const dedupeEbayImportImageUrls = (urls = []) => {
  const result = [];
  const seen = new Set();
  (Array.isArray(urls) ? urls : []).forEach((entry) => {
    const url = String(entry || "").trim();
    if (!url) return;
    const identity = normalizeEbayImportImageIdentity(url) || url;
    if (seen.has(identity)) return;
    seen.add(identity);
    result.push(url);
  });
  return result;
};

const getFileExtension = (name = "") => {
  const match = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/i);
  return match ? String(match[1] || "").toLowerCase() : "";
};

const shouldConvertImageToJpeg = (file) => {
  const mime = String(file?.type || "").toLowerCase();
  const ext = getFileExtension(file?.name || "");
  if (mime && PUBLISH_IMAGE_ALLOWED_TYPES.has(mime)) return false;
  if (ext && PUBLISH_IMAGE_ALLOWED_EXTENSIONS.has(ext)) return false;
  return true;
};

const normalizePriceInput = (value) => {
  const raw = String(value || "");
  const cleaned = raw.replace(/[^\d.,]/g, "");
  const firstSeparatorIndex = cleaned.search(/[.,]/);
  if (firstSeparatorIndex < 0) return cleaned;
  const integerPart = cleaned.slice(0, firstSeparatorIndex).replace(/[^\d]/g, "");
  const separator = cleaned[firstSeparatorIndex] || ".";
  const decimalPart = cleaned.slice(firstSeparatorIndex + 1).replace(/[^\d]/g, "").slice(0, 2);
  if (!integerPart && !decimalPart) return "";
  return decimalPart ? `${integerPart}${separator}${decimalPart}` : `${integerPart}${separator}`;
};

const loadImageFromBlob = (blob) => new Promise((resolve, reject) => {
  const objectUrl = URL.createObjectURL(blob);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error("decode-failed"));
  };
  image.src = objectUrl;
});

const dataUrlToJpegBlob = (dataUrl) => {
  const raw = String(dataUrl || "");
  if (!raw.startsWith("data:image/jpeg")) return null;
  const commaIndex = raw.indexOf(",");
  if (commaIndex < 0) return null;
  const base64 = raw.slice(commaIndex + 1);
  if (!base64) return null;
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new Blob([bytes], { type: "image/jpeg" });
  } catch (error) {
    return null;
  }
};

const decodeImageDrawable = async (file) => {
  try {
    const image = await loadImageFromBlob(file);
    return {
      drawable: image,
      width: Number(image.naturalWidth || image.width || 0),
      height: Number(image.naturalHeight || image.height || 0),
      release: () => {}
    };
  } catch (decodeError) {
    if (typeof createImageBitmap !== "function") throw decodeError;
    const bitmap = await createImageBitmap(file);
    return {
      drawable: bitmap,
      width: Number(bitmap.width || 0),
      height: Number(bitmap.height || 0),
      release: () => {
        if (typeof bitmap.close === "function") {
          try {
            bitmap.close();
          } catch (error) {
            // ignore release errors
          }
        }
      }
    };
  }
};

const convertImageFileToJpeg = async (file) => {
  if (!shouldConvertImageToJpeg(file)) {
    return { file, converted: false };
  }
  const decoded = await decodeImageDrawable(file);
  const width = Number(decoded.width || 0);
  const height = Number(decoded.height || 0);
  if (!width || !height) {
    decoded.release();
    throw new Error("invalid-image-size");
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("canvas-unavailable");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(decoded.drawable, 0, 0, width, height);
  decoded.release();

  let jpegBlob = await new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.92);
  });
  if (!jpegBlob) {
    const jpegDataUrl = canvas.toDataURL("image/jpeg", 0.92);
    jpegBlob = dataUrlToJpegBlob(jpegDataUrl);
  }
  if (!jpegBlob) {
    throw new Error("jpeg-conversion-failed");
  }

  const originalName = String(file?.name || "image");
  const baseName = originalName.replace(/\.[^.]+$/, "") || "image";
  const convertedFile = new File([jpegBlob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now()
  });
  return {
    file: convertedFile,
    converted: true,
    sourceName: originalName,
    targetName: convertedFile.name
  };
};

const prepareImageFilesForPublish = async (files = [], { onProgress } = {}) => {
  const accepted = [];
  const converted = [];
  const failed = [];

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index];
    if (typeof onProgress === "function") {
      try {
        onProgress({ index: index + 1, total: files.length, fileName: file?.name || "" });
      } catch (error) {
        // ignore progress callback errors
      }
    }
    try {
      const result = await convertImageFileToJpeg(file);
      // Deliberately not downscaled here. Listing photos are the seller's own
      // product shots, publishing was never the slow path, and the shrink that
      // helps chat attachments only costs quality on an ad.
      accepted.push(result.file);
      if (result.converted) {
        converted.push({
          sourceName: result.sourceName || file?.name || "",
          targetName: result.targetName || result.file?.name || ""
        });
      }
    } catch (error) {
      failed.push({
        name: String(file?.name || "image"),
        reason: String(error?.message || "conversion-failed")
      });
    }
  }

  return { files: accepted, converted, failed };
};

// Names the reason rather than guessing at it. The old wording told the seller to
// use JPG, which sent them in circles the one time this fired on a batch of JPGs:
// the cause was a broken build, not the files. JPG, PNG and GIF are passed through
// untouched, so a rejection here is always either an undecodable file or a format
// the browser cannot convert.
const describeFailedImages = (failed = []) => {
  const names = failed.slice(0, 4).map((item) => item.name).join(", ");
  const more = failed.length > 4 ? tf` и ещё ${failed.length - 4}` : "";
  const reasons = [...new Set(failed.map((item) => String(item?.reason || "").trim()).filter(Boolean))];
  const why = reasons.length === 1 ? tf` Причина: ${reasons[0]}` : "";
  return tf`Не удалось подготовить фото: ${names}${more}.${why}`;
};

const computeFastFingerprint = (file) => {
  if (!file || typeof file.slice !== "function") return Promise.resolve("");
  const maxSlice = 64 * 1024;
  const readSlice = async (blob) => new Uint8Array(await blob.arrayBuffer());
  const foldBytes = (seed, bytes) => {
    let hash = seed >>> 0;
    for (let index = 0; index < bytes.length; index += 1) {
      hash ^= bytes[index];
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };
  return (async () => {
    const head = await readSlice(file.slice(0, maxSlice));
    const tailStart = Math.max(0, Number(file.size || 0) - maxSlice);
    const tail = tailStart > 0 ? await readSlice(file.slice(tailStart)) : new Uint8Array();
    let hash = 2166136261;
    hash = foldBytes(hash, head);
    hash = foldBytes(hash, tail);
    hash ^= Number(file.size || 0) >>> 0;
    hash = Math.imul(hash, 16777619) >>> 0;
    const type = String(file.type || "").toLowerCase();
    for (let i = 0; i < type.length; i += 1) {
      hash ^= type.charCodeAt(i);
      hash = Math.imul(hash, 16777619) >>> 0;
    }
    return `${Number(file.size || 0)}:${hash.toString(16).padStart(8, "0")}`;
  })().catch(() => "");
};

const dedupeImageFilesByFingerprint = async (files = []) => {
  const uniqueFiles = [];
  const duplicateNames = [];
  const seen = new Set();
  for (const file of Array.isArray(files) ? files : []) {
    const fingerprint = await computeFastFingerprint(file);
    const key = fingerprint || `${String(file?.name || "")}:${Number(file?.size || 0)}`;
    if (seen.has(key)) {
      duplicateNames.push(String(file?.name || ""));
      continue;
    }
    seen.add(key);
    uniqueFiles.push(file);
  }
  return { files: uniqueFiles, duplicateNames };
};

// Дерево категорий, уже раскрытое в этой вкладке браузера. Подкатегории
// приходят по требованию и раньше жили только внутри окна — после закрытия
// выбранную ветку было не из чего восстановить. Отсюда окно и начинает.
let sessionCategoryTree = [];

// Новый PLZ — прежний населённый пункт к нему не относится.
// Значок категории — по названию рубрики KL (сначала по самой рубрике, потом
// по разделу). Нет совпадения — коробка.
const CATEGORY_ICON_RULES = [
  [/handy|telefon|smartphone/i, Smartphone],
  [/notebook|laptop/i, Laptop],
  [/\bpc\b|computer|software/i, Monitor],
  [/tablet|reader/i, Tablet],
  [/audio|hifi|kopfhörer|lautsprecher/i, Headphones],
  [/konsole|videospiel/i, Gamepad2],
  [/foto|kamera/i, Camera],
  [/\btv\b|fernseh|video/i, Tv],
  [/haushaltsger|küche/i, Utensils],
  [/uhr|schmuck/i, Watch],
  [/fahrrad|bike/i, Bike],
  [/boot|schiff/i, Sailboat],
  [/auto|kfz|motorrad|wohnwagen|nutzfahrzeug|anhänger/i, Car],
  [/möbel|wohnzimmer|schlafzimmer|sofa/i, Sofa],
  [/garten|pflanze/i, Flower2],
  [/heimwerk|werkzeug|bauen/i, Hammer],
  [/lampe|licht|beleuchtung/i, Lamp],
  [/haus|wohnen|immobil|wohnung/i, Home],
  [/schuhe/i, Footprints],
  [/mode|kleidung|damen|herren|beauty|gesundheit/i, Shirt],
  [/baby|kind|spielzeug/i, Baby],
  [/buch|bücher|zeitschrift/i, BookOpen],
  [/musik|instrument|\bcd|film|dvd/i, Music],
  [/haustier|hund|katze|tier/i, PawPrint],
  [/ticket|eintritt/i, Ticket],
  [/sport|fitness/i, Dumbbell],
  [/camping|outdoor/i, Tent],
  [/sammeln|antiquit|kunst/i, Gem],
  [/modellbau|basteln|hobby|spiel/i, Puzzle],
  [/\bjob|stellen/i, Briefcase],
  [/unterricht|kurs/i, GraduationCap],
  [/dienstleistung|reparatur|service/i, Wrench],
  [/nachbarschaft|hilfe/i, HeartHandshake],
  [/verschenken|tauschen/i, Gift]
];
const matchCategoryIcon = (text) => {
  const value = String(text || "");
  if (!value) return null;
  const found = CATEGORY_ICON_RULES.find(([pattern]) => pattern.test(value));
  return found ? found[1] : null;
};
const categoryIconFor = (name, trail) => matchCategoryIcon(name) || matchCategoryIcon(trail) || PackageGlyph;
// Значки шагов мастера.
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(String(navigator.platform || navigator.userAgent || ""));
const MOD_KEY_LABEL = IS_MAC ? "⌘" : "Ctrl";

const withPostalCode = (prev, code) => (
  String(prev?.postalCode || "").trim() === String(code || "").trim()
    ? { ...prev, postalCode: code }
    : { ...prev, postalCode: code, locationId: "", locationName: "" }
);

const AdModal = ({
  isOpen,
  dialogState = "open",
  isPhoneView = false,
  onClose,
  onSubmit,
  onSaveDraft,
  publishing,
  accounts,
  categories,
  extraFields,
  extraFieldValues,
  setExtraFieldValues,
  loadingExtraFields,
  extraFieldsError,
  newAd,
  setNewAd,
  adImages,
  setAdImages,
  draftImageUrls = [],
  setDraftImageUrls,
  photoOrder = [],
  setPhotoOrder
}) => {
  if (!isOpen) return null;

  // Язык рубрик и параметров (настройка «Язык категорий»): меняется только
  // показ, в объявление уходят прежние номера категорий и коды значений.
  const categoryLangStamp = useCategoryLang();
  const [categoryPath, setCategoryPath] = useState([]);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [localCategories, setLocalCategories] = useState(() => sessionCategoryTree);
  const [didRestorePath, setDidRestorePath] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  // Перестановка фото — как в окне правки: левая кнопка мыши (со сдвига на 5 px)
  // и кнопки на фото. Номер — позиция в общей сетке (снимки черновика и свои).
  const [photoDragActive, setPhotoDragActive] = useState(false);
  // Фото, открытое в редакторе (components/PhotoEditor.jsx): плитка сетки.
  const [photoEditorItem, setPhotoEditorItem] = useState(null);
  const [photoDragIndex, setPhotoDragIndex] = useState(null);
  const [loadingPlz, setLoadingPlz] = useState(false);
  const [loadingProxyPlz, setLoadingProxyPlz] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // Раскрыта ли строка «Местоположение», тянут ли файлы на окно.
  const [locationOpen, setLocationOpen] = useState(false);
  const [filesOverModal, setFilesOverModal] = useState(false);
  const fileDragDepthRef = useRef(0);
  // Горячие клавиши и вставка фото: ⌘/Ctrl+Enter — «Опубликовать»,
  // ⌘/Ctrl+S — в черновик, ⌘/Ctrl+V с картинкой в буфере — фото в объявление.
  // Обработчики берутся из ref: так слушатель один на всё время окна.
  const hotkeyActionsRef = useRef({ next: () => {}, save: () => {}, addFiles: () => {} });
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      if (event.key === "Enter") {
        event.preventDefault();
        hotkeyActionsRef.current.next();
      } else if (event.code === "KeyS" || String(event.key).toLowerCase() === "s") {
        event.preventDefault();
        hotkeyActionsRef.current.save();
      }
    };
    const onPaste = (event) => {
      const items = Array.from(event.clipboardData?.items || []);
      const files = items
        .filter((item) => item.kind === "file" && String(item.type || "").toLowerCase().startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter(Boolean);
      if (!files.length) return;
      event.preventDefault();
      hotkeyActionsRef.current.addFiles(files);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("paste", onPaste);
    };
  }, [isOpen]);
  // Название и текст растут по содержимому — без полосы прокрутки внутри.
  const titleFieldRef = useRef(null);
  const descriptionFieldRef = useRef(null);
  useEffect(() => {
    [titleFieldRef.current, descriptionFieldRef.current].forEach((node) => {
      if (!node) return;
      node.style.height = "auto";
      node.style.height = `${node.scrollHeight}px`;
    });
  }, [newAd.title, newAd.description, isPhoneView]);
  const previewPresence = useModalPresence(showPreview);
  const [categoryChildrenError, setCategoryChildrenError] = useState("");
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [ebayUrl, setEbayUrl] = useState("");
  const [grabSiteId, setGrabSiteId] = useState("ebay");
  const [ebayImporting, setEbayImporting] = useState(false);
  const [ebayImportStatus, setEbayImportStatus] = useState("");
  const [ebayImportError, setEbayImportError] = useState("");
  const [categoryQuickQuery, setCategoryQuickQuery] = useState("");
  const [categoryQuickOpen, setCategoryQuickOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  // Подсказка категории по названию: та же модель, что отвечает в сообщениях.
  // forTitle хранит название, под которое подсказка получена, — по нему видно,
  // что название с тех пор изменилось и подсказку стоит обновить.
  const [categorySuggest, setCategorySuggest] = useState({
    status: "idle", items: [], error: "", forTitle: ""
  });
  // Уникализация текста: та же модель переводит на немецкий и переписывает
  // название с описанием другими словами. backup — что стояло до нажатия:
  // переписанное можно вернуть одним нажатием.
  const [rewriteByField, setRewriteByField] = useState({
    title: { status: "idle", error: "", backup: null },
    description: { status: "idle", error: "", backup: null }
  });
  // Объявление по фото: модель смотрит снимки и пишет название, описание,
  // подбирает категорию, выбирает обложку и отмечает, что с фото не так.
  const [photoDescribe, setPhotoDescribe] = useState({ status: "idle", error: "", summary: "", notes: {} });
  // Escape: сначала раскрытый список или предпросмотр, потом само окно.
  const escapeActive = isOpen && dialogState !== "closed";
  useEscapeLayer(escapeActive, () => onClose());
  useEscapeLayer(escapeActive && accountDropdownOpen, () => setAccountDropdownOpen(false));
  useEscapeLayer(escapeActive && categoryPickerOpen, () => setCategoryPickerOpen(false));
  useEscapeLayer(escapeActive && categoryQuickOpen, () => setCategoryQuickOpen(false));
  useEscapeLayer(escapeActive && showPreview, () => setShowPreview(false));
  const aiPrefs = useAiPrefs();
  const categoryListRef = useRef(null);
  const imageInputRef = useRef(null);
  const accountDropdownRef = useRef(null);
  const pendingChildrenRef = useRef(new Map());
  const prefetchRootRef = useRef("");
  const prefetchInFlightRef = useRef(false);
  const prefetchedTargetsRef = useRef(new Set());
  const prefetchTokenRef = useRef(0);
  const photoDragRef = useRef({ active: false, pending: false, index: -1, startX: 0, startY: 0 });
  const lastAutofillAccountIdRef = useRef("");
  // Какой PLZ форма подставила сама из аккаунта — его можно заменить при смене
  // аккаунта, введённый руками нельзя.
  const autofilledPostalCodeRef = useRef("");

  // Счётчики названия и описания.
  const titleLength = (newAd.title || "").length;
  const descriptionLength = (newAd.description || "").length;


  // Зависимые параметры категории. Kleinanzeigen требует такое поле только при
  // определённом значении другого: например в Haustiere → Zubehör выбор
  // «Art = Pferde» добавляет обязательное «Art des Zubehörs» со своим списком,
  // а у остальных значений этого поля нет вовсе. Пока панель его не показывала,
  // публикация с Pferde отклонялась (reject.empty на zubehoer.type_s).
  const resolveExtraFieldOptions = (field) => {
    if (!field?.dependsOn) return Array.isArray(field?.options) ? field.options : [];
    const parentValue = String(extraFieldValues?.[field.dependsOn] || "").trim();
    if (!parentValue) return [];
    const byParent = field.optionsByParent && typeof field.optionsByParent === "object"
      ? field.optionsByParent
      : {};
    return Array.isArray(byParent[parentValue]) ? byParent[parentValue] : [];
  };
  // Зависимое поле показывается, только когда у выбранного значения родителя
  // действительно есть варианты; обязательным оно считается там же.
  const visibleExtraFields = (Array.isArray(extraFields) ? extraFields : [])
    .filter((field) => !field?.dependsOn || resolveExtraFieldOptions(field).length > 0);

  const missingRequiredExtraFields = visibleExtraFields
    .filter((field) => field?.required)
    .filter((field) => {
      const key = field.name || field.label;
      const value = key ? extraFieldValues?.[key] : "";
      return !String(value ?? "").trim();
    });
  const hasSelectedCategory = Boolean(newAd?.categoryId || newAd?.categoryUrl);
  const submitBlockedByFields =
    hasSelectedCategory && (loadingExtraFields || Boolean(extraFieldsError) || missingRequiredExtraFields.length > 0);

  // Publishing is never blocked (design 2a §75): clicking with unfilled required
  // parameters highlights them and scrolls the column to the first one instead.
  const bodyScrollRef = useRef(null);
  const [highlightMissing, setHighlightMissing] = useState(false);
  const handlePublishClick = () => {
    if (submitBlockedByFields) {
      setHighlightMissing(true);
      const node = bodyScrollRef.current;
      const target = node?.querySelector?.("[data-missing-required='1']");
      // Прокручивается та колонка (или на телефоне всё окно), где поле.
      if (node && target) target.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setHighlightMissing(false);
    onSubmit?.();
  };
  useEffect(() => {
    if (highlightMissing && !submitBlockedByFields) setHighlightMissing(false);
  }, [highlightMissing, submitBlockedByFields]);

  // Footer status: mirror everything handleCreateAd actually requires, not just
  // the category parameters — otherwise an empty form still read "Готово".
  const photoCount = (Array.isArray(adImages) ? adImages.length : 0)
    + (Array.isArray(draftImageUrls) ? draftImageUrls.filter((item) => String(item || "").trim()).length : 0);
  const missingBasics = [
    !newAd?.accountId && t("аккаунт"),
    !String(newAd?.title || "").trim() && t("название"),
    !String(newAd?.description || "").trim() && t("описание"),
    !String(newAd?.price || "").trim() && t("цену"),
    !hasSelectedCategory && t("категорию"),
    !photoCount && t("фото")
  ].filter(Boolean);
  const missingRequiredCount = missingBasics.length + missingRequiredExtraFields.length;
  const notReadyToPublish = missingRequiredCount > 0 || loadingExtraFields || Boolean(extraFieldsError);
  const missingRequiredLabel = (() => {
    if (loadingExtraFields) return t("Загружаем параметры категории...");
    if (extraFieldsError) return t("Не удалось загрузить параметры категории");
    if (missingBasics.length) {
      const head = missingBasics.slice(0, 2).join(", ");
      const rest = missingRequiredCount - Math.min(2, missingBasics.length);
      return rest > 0 ? tf`Заполните: ${head} и ещё ${rest}` : tf`Заполните: ${head}`;
    }
    const n = missingRequiredCount;
    const word = (n % 10 === 1 && n % 100 !== 11)
      ? t("обязательный параметр")
      : ((n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) ? t("обязательных параметра") : t("обязательных параметров"));
    return tf`Осталось заполнить ${n} ${word}`;
  })();

  const formatAccountLabel = (account) => {
    const name = account.profileName || account.username || t("Аккаунт");
    const email = account.profileEmail || "";
    return email ? `${name} (${email})` : name;
  };

  const normalizeSatisfactionBadge = (value) => {
    const normalized = String(value || "").trim().toUpperCase();
    if (["TOP", "OK", "NAJA", "MIX"].includes(normalized)) return normalized;
    return "MIX";
  };

  const getAccountProxyLabel = (account) =>
    String(account?.proxy || account?.proxyName || "").trim() || t("Без прокси");

  const selectedAccountMeta = accounts.find((account) => String(account.id) === String(newAd.accountId)) || null;
  const previewAccountName = selectedAccountMeta
    ? String(selectedAccountMeta.profileName || selectedAccountMeta.username || t("Аккаунт")).trim() || t("Аккаунт")
    : t("Аккаунт не выбран");
  const previewAccountEmail = selectedAccountMeta
    ? String(selectedAccountMeta.profileEmail || "").trim()
    : "";
  const isPhoneLayout = Boolean(isPhoneView);
  // Та же защита, что у окна добавления аккаунта: фон под открытой
  // карточкой не прокручивается.
  useEffect(() => {
    if (!isOpen) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isOpen]);
  const overlayRef = useRef(null);
  useModalTouchGuard(overlayRef, isOpen && isPhoneLayout);
  const filledPreviewExtraFields = visibleExtraFields
    .map((field) => {
      const key = field?.name || field?.label;
      const value = key ? String(extraFieldValues?.[key] || "").trim() : "";
      if (!value) return null;
      // В значении — код Kleinanzeigen («ja», «sehr_gut»); показываем подпись
      // выбранного варианта, если она есть.
      const option = resolveExtraFieldOptions(field).find((item) => String(item?.value ?? "") === value);
      return {
        label: field?.label ? catText(String(field.label).trim()) : String(key || t("Параметр")).trim(),
        value: option?.label ? catText(option.label) : value
      };
    })
    .filter(Boolean)
    .slice(0, 10);

  // Название, как его отдаёт Kleinanzeigen, — для поиска: искать можно и
  // по-немецки, когда рубрики показаны на другом языке.
  const getCategoryRawLabel = (category) => {
    if (!category) return "";
    const raw =
      category.name ||
      category.label ||
      category.title ||
      category.categoryName ||
      category.displayName;
    const text = String(raw || "").trim();
    if (text) return text;
    const fallback = category.id || category.url || "";
    return String(fallback);
  };
  const getCategoryLabel = (category) => catText(getCategoryRawLabel(category));

  const isNumericCategoryId = (value) => /^\d+$/.test(String(value || ""));

  const looksLikeCategoryUrl = (value) => /\/c\d+(?:\/|$)/i.test(String(value || ""));

  const isAttributeOptionNode = (node, parent) => {
    if (!node || !parent) return false;
    const parentId = parent.id !== undefined && parent.id !== null ? String(parent.id) : "";
    if (!isNumericCategoryId(parentId)) return false;
    const idValue = node.id !== undefined && node.id !== null ? String(node.id) : "";
    const urlValue = node.url ? String(node.url) : "";
    const nameValue = String(node.name || node.label || "").trim().toLowerCase();
    const normalizedId = idValue.trim().toLowerCase();
    if (isNumericCategoryId(normalizedId)) return false;
    if (looksLikeCategoryUrl(urlValue)) return false;
    if (Array.isArray(node.children) && node.children.length > 0) return false;
    if (!normalizedId || !nameValue) return false;
    // Keep real subcategories with slug-like ids (e.g. "drucker_scanner").
    // Filter only very small option-like values that are likely field options.
    const optionLikeValues = new Set(["ja", "nein", "yes", "no", "new", "used", "other"]);
    return optionLikeValues.has(normalizedId) || optionLikeValues.has(nameValue);
  };

  const filterCategoryChildren = (nodes, parent) =>
    (Array.isArray(nodes) ? nodes : []).filter((node) => !isAttributeOptionNode(node, parent));

  const getCategoryValue = (category) => {
    if (!category) return "";
    const id = category.id !== undefined && category.id !== null ? String(category.id) : "";
    const url = category.url ? String(category.url) : "";
    if (id && isNumericCategoryId(id)) return id;
    if (url) return url;
    return id;
  };

  const getNodeKey = (node) => {
    if (!node) return "";
    const value = getCategoryValue(node);
    if (value) return `value:${value}`;
    if (node.name) return `name:${String(node.name)}`;
    return "";
  };

  const mergeCategoryTrees = (prevNodes, nextNodes) => {
    if (!Array.isArray(nextNodes) || !nextNodes.length) {
      return Array.isArray(prevNodes) ? prevNodes : [];
    }
    const prev = Array.isArray(prevNodes) ? prevNodes : [];
    const prevMap = new Map();
    for (const node of prev) {
      const key = getNodeKey(node);
      if (key) prevMap.set(key, node);
    }
    const used = new Set();
    const merged = nextNodes.map((node) => {
      const key = getNodeKey(node);
      if (key) used.add(key);
      const prevNode = key ? prevMap.get(key) : null;
      const nextChildren = Array.isArray(node.children) ? node.children : [];
      const prevChildren = prevNode?.children || [];
      const mergedChildren = nextChildren.length
        ? mergeCategoryTrees(prevChildren, nextChildren)
        : prevChildren;
      const childrenLoaded = Boolean(node?.childrenLoaded || prevNode?.childrenLoaded);
      return {
        ...(prevNode || {}),
        ...node,
        children: mergedChildren,
        childrenLoaded
      };
    });
    for (const [key, node] of prevMap.entries()) {
      if (!used.has(key)) merged.push(node);
    }
    return merged;
  };

  const buildPathToNode = (nodes, targetValue, path = []) => {
    if (!targetValue || !Array.isArray(nodes)) return [];
    const target = String(targetValue);
    for (const node of nodes) {
      const matches =
        (node.id !== undefined && node.id !== null && String(node.id) === target) ||
        (node.url && String(node.url) === target);
      const nextValue = getCategoryValue(node);
      const nextPath = nextValue ? [...path, nextValue] : path;
      if (matches) return nextPath;
      if (node.children?.length) {
        const childPath = buildPathToNode(node.children, targetValue, nextPath);
        if (childPath.length) return childPath;
      }
    }
    return [];
  };

  const findNode = (nodes, value) => {
    if (!value) return null;
    for (const node of nodes) {
      if (String(node.id) === String(value) || String(node.url) === String(value)) return node;
      if (node.children?.length) {
        const found = findNode(node.children, value);
        if (found) return found;
      }
    }
    return null;
  };

  const getBreadcrumbForPath = (path) => {
    const breadcrumb = [];
    let current = localCategories;
    for (const pathItem of path) {
      const found = current.find((cat) =>
        (cat.id && String(cat.id) === String(pathItem)) ||
        (cat.url && String(cat.url) === String(pathItem))
      );
      if (!found) break;
      breadcrumb.push(found);
      current = filterCategoryChildren(found.children || [], found);
    }
    return breadcrumb;
  };

  const resolveCategorySelection = (path) => {
    const breadcrumb = getBreadcrumbForPath(path);
    const selectedNode = breadcrumb.length ? breadcrumb[breadcrumb.length - 1] : null;
    const numericNode = [...breadcrumb].reverse().find((node) => /^\d+$/.test(String(node?.id || "")));
    return {
      selectedNode,
      numericNode: numericNode || selectedNode
    };
  };

  const buildQuickCategoryOptions = (nodes, path = [], parentNode = null) => {
    const source = filterCategoryChildren(nodes, parentNode);
    const list = [];
    for (const node of source) {
      const value = getCategoryValue(node);
      const label = getCategoryLabel(node);
      if (!value || !label) continue;
      const rawLabel = getCategoryRawLabel(node);
      const nextPath = [...path, { value: String(value), label, rawLabel }];
      const fullPathLabel = nextPath.map((item) => item.label).join(" / ");
      const rawPathLabel = nextPath.map((item) => item.rawLabel).join(" / ");
      list.push({
        value: String(value),
        label,
        pathValues: nextPath.map((item) => item.value),
        fullPathLabel,
        searchText: rawPathLabel === fullPathLabel
          ? fullPathLabel.toLowerCase()
          : `${fullPathLabel}\n${rawPathLabel}`.toLowerCase()
      });
      if (Array.isArray(node.children) && node.children.length) {
        list.push(...buildQuickCategoryOptions(node.children, nextPath, node));
      }
    }
    return list;
  };

  function syncQuickCategoryQueryFromPath(path) {
    const labels = getBreadcrumbForPath(Array.isArray(path) ? path : [])
      .map((item) => getCategoryLabel(item))
      .filter(Boolean);
    setCategoryQuickQuery(labels.join(" / "));
  }

  useEffect(() => {
    setLocalCategories((prev) => mergeCategoryTrees(prev, categories || []));
  }, [categories]);

  useEffect(() => {
    sessionCategoryTree = localCategories;
  }, [localCategories]);

  useEffect(() => {
    if (isOpen) {
      setCategoryPath([]);
      setDidRestorePath(false);
      setCategoryChildrenError("");
      setAccountDropdownOpen(false);
      setEbayImporting(false);
      setEbayImportStatus("");
      setEbayImportError("");
      setCategoryQuickQuery("");
      setCategoryQuickOpen(false);
      // Категория в newAd не сбрасывается: после закрытия крестиком или
      // Escape она остаётся, как название и описание, и ветка ниже
      // восстанавливается по сохранённому пути. Чистят форму только
      // публикация и сохранение черновика.
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !accountDropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target)) {
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, accountDropdownOpen]);

  useEffect(() => {
    if (!adImages || adImages.length === 0) {
      setImagePreviews([]);
      return undefined;
    }

    const previews = adImages.map((file) => ({
      file,
      url: URL.createObjectURL(file)
    }));
    setImagePreviews(previews);

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [adImages]);

  const stopPhotoDrag = () => {
    const drag = photoDragRef.current;
    if (!drag.active && !drag.pending) return;
    photoDragRef.current = { active: false, pending: false, index: -1, startX: 0, startY: 0 };
    setPhotoDragActive(false);
    setPhotoDragIndex(null);
  };

  useEffect(() => {
    const handlePointerMove = (event) => {
      const drag = photoDragRef.current;
      if (!drag.active && !drag.pending) return;
      if (!drag.active) {
        if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5) return;
        photoDragRef.current = { ...drag, active: true, pending: false };
        setPhotoDragActive(true);
        setPhotoDragIndex(drag.index);
      }
      const hovered = document.elementFromPoint(event.clientX, event.clientY);
      const tile = hovered && typeof hovered.closest === "function" ? hovered.closest("[data-photo-index]") : null;
      if (tile) movePhotoDragTo(Number(tile.getAttribute("data-photo-index")));
    };
    const stop = () => stopPhotoDrag();
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    window.addEventListener("blur", stop);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("blur", stop);
    };
  }, []);

  // Выбранные и перетащенные фото добавляются к уже стоящим, а не заменяют
  // их. Раньше каждый выбор файлов ставил в объявление только новые: к фото,
  // забранным по ссылке, нельзя было добавить своё — они пропадали, оставалось
  // одно добавленное. Повтор того же снимка отбрасывается по отпечатку файла,
  // а сверх общего лимита (вместе с фото черновика) лишнее не добавляется.
  const adImagesRef = useRef(adImages);
  adImagesRef.current = adImages;
  const appendAdImageFiles = async (selected) => {
    const prepared = await prepareImageFilesForPublish(selected);
    if (prepared.failed.length) alert(describeFailedImages(prepared.failed));
    if (!prepared.files.length) return;
    const current = Array.isArray(adImagesRef.current) ? adImagesRef.current : [];
    const { files: unique, duplicateNames } = await dedupeImageFilesByFingerprint([...current, ...prepared.files]);
    const draftCount = Array.isArray(draftImageUrls)
      ? draftImageUrls.filter((item) => String(item || "").trim()).length
      : 0;
    const room = Math.max(0, AD_MAX_IMAGES - draftCount);
    const next = unique.slice(0, room);
    const added = next.length - current.length;
    const skippedByLimit = unique.length - next.length;
    if (added > 0) setAdImages(next);
    if (skippedByLimit > 0) {
      alert(tf`Можно не больше ${AD_MAX_IMAGES} фото в объявлении — не добавлено: ${skippedByLimit}.`);
    } else if (added <= 0 && duplicateNames.length) {
      alert(t("Эти фото уже добавлены."));
    }
  };

  const handleImageInputChange = async (event) => {
    const selected = Array.from(event?.target?.files || []).filter((file) =>
      String(file?.type || "").toLowerCase().startsWith("image/")
    );
    if (event?.target) event.target.value = "";
    if (!selected.length) return;
    await appendAdImageFiles(selected);
  };

  const handleImageDrop = async (event) => {
    event.preventDefault();
    const selected = Array.from(event?.dataTransfer?.files || []).filter((file) =>
      String(file?.type || "").toLowerCase().startsWith("image/")
    );
    if (!selected.length) return;
    await appendAdImageFiles(selected);
  };

  const fetchEbayImageFile = async (url, index) => {
    const response = await apiFetch(`/api/ads/ebay-image?url=${encodeURIComponent(url)}`, {
      timeoutMs: 45000,
      retry: true
    });
    if (!response.ok) {
      let details = "";
      try {
        details = (await response.text()).trim();
      } catch (error) {
        details = "";
      }
      throw new Error(details || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const contentType = response.headers.get("content-type") || blob.type || "image/jpeg";
    const ext = detectImageExtension(contentType, url);
    const name = `ebay-${String(index + 1).padStart(2, "0")}${ext}`;
    return new File([blob], name, { type: contentType, lastModified: Date.now() });
  };

  const handleEbayGrab = async () => {
    const sourceUrl = String(ebayUrl || "").trim();
    const site = GRAB_SITES.find((item) => item.id === grabSiteId) || GRAB_SITES[0];
    if (!sourceUrl) {
      setEbayImportError(tf`Вставьте ссылку на товар ${site.label}.`);
      setEbayImportStatus("");
      return;
    }
    // Площадку определяем по самой ссылке: если она с другой, чем выбрана,
    // переключаемся молча — так надёжнее, чем просить исправить выбор.
    const detected = detectGrabSiteId(sourceUrl);
    const effective = detected
      ? (GRAB_SITES.find((item) => item.id === detected) || site)
      : site;
    if (detected && detected !== grabSiteId) setGrabSiteId(detected);
    if (!detected) {
      setEbayImportError(tf`Ссылка должна вести на страницу товара ${site.label}.`);
      setEbayImportStatus("");
      return;
    }

    setEbayImporting(true);
    setEbayImportError("");
    setEbayImportStatus(tf`Забираю данные со страницы ${effective.label}...`);

    try {
      const isEbay = effective.id === "ebay";
      const result = await apiFetchJson(isEbay ? "/api/ads/ebay-grab" : "/api/ads/grab", {
        method: "POST",
        // Больше, чем срок у сервера: иначе панель сдаётся раньше, чем он успеет
        // ответить внятной причиной, и вместо неё показывает свой таймаут. У
        // eBay путей больше — прямой запрос, отпечаток Chrome, прокси, браузер,
        // который ещё и ждёт, пока его пропустит проверка, — и срок длиннее.
        timeoutMs: isEbay ? 150000 : 90000,
        retry: false,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: sourceUrl })
      });
      // У eBay ответ вложен в item, у остальных площадок поля лежат сразу.
      const item = isEbay ? (result?.item || {}) : (result || {});
      const nextTitle = String(item?.title || "").trim();
      const nextDescription = String(item?.description || "").trim();
      const imageUrls = Array.isArray(item?.imageUrls || item?.images)
        ? (item.imageUrls || item.images).map((entry) => String(entry || "").trim()).filter(Boolean)
        : [];
      const dedupedImageUrls = dedupeEbayImportImageUrls(imageUrls);

      if (!nextTitle && !nextDescription && dedupedImageUrls.length === 0) {
        throw new Error(t("Граббер не нашел данных на странице."));
      }

      if (nextTitle || nextDescription) {
        setNewAd((prev) => ({
          ...prev,
          title: (nextTitle || prev.title || "").slice(0, 65),
          description: (nextDescription || prev.description || "").slice(0, 4000)
        }));
      }

      const files = [];
      const limitedUrls = dedupedImageUrls.slice(0, EBAY_IMPORT_MAX_IMAGES);
      for (let index = 0; index < limitedUrls.length; index += 1) {
        const imageUrl = limitedUrls[index];
        setEbayImportStatus(tf`Загружаю фото ${index + 1}/${limitedUrls.length}...`);
        try {
          const file = await fetchEbayImageFile(imageUrl, index);
          if (file) files.push(file);
        } catch (error) {
          adModalDebugWarn("[eBay import] image fetch failed", imageUrl, error?.message || error);
        }
      }

      const dedupedFetched = await dedupeImageFilesByFingerprint(files);
      const duplicateFetchedCount = Math.max(0, files.length - dedupedFetched.files.length);
      if (duplicateFetchedCount > 0) {
        adModalDebugLog(`[eBay import] dropped duplicate files: ${duplicateFetchedCount}`);
      }

      let preparedFiles = dedupedFetched.files;
      let convertedImages = [];
      let failedImages = [];
      if (dedupedFetched.files.length > 0) {
        const prepared = await prepareImageFilesForPublish(dedupedFetched.files, {
          onProgress: ({ index, total }) => {
            setEbayImportStatus(tf`Подготавливаю фото ${index}/${total}...`);
          }
        });
        preparedFiles = prepared.files;
        convertedImages = prepared.converted;
        failedImages = prepared.failed;
        if (preparedFiles.length > 0) {
          setAdImages(preparedFiles);
        }
      }

      const stats = [];
      if (nextTitle) stats.push(t("название"));
      if (nextDescription) stats.push(t("описание"));
      if (preparedFiles.length > 0) stats.push(tf`фото: ${preparedFiles.length}`);
      if (duplicateFetchedCount > 0) stats.push(tf`дубликаты убраны: ${duplicateFetchedCount}`);
      if (convertedImages.length > 0) stats.push(tf`конвертировано в JPG: ${convertedImages.length}`);
      setEbayImportStatus(stats.length
        ? tf`Импорт завершен: ${stats.join(", ")}.`
        : t("Импорт завершен."));
      if (!preparedFiles.length && dedupedImageUrls.length) {
        setEbayImportError(t("Данные объявления загружены, но фото скачать/подготовить не удалось."));
      } else if (failedImages.length) {
        const failedNames = failedImages.slice(0, 4).map((item) => item.name).join(", ");
        setEbayImportError(tf`Часть фото пропущена: ${failedNames}.`);
      }
    } catch (error) {
      // Сообщения сервера — по-русски; t() переводит те, что есть в словаре.
      setEbayImportError(t(error?.message) || t("Не удалось загрузить объявление."));
      setEbayImportStatus("");
    } finally {
      setEbayImporting(false);
    }
  };

  // Отредактированное фото встаёт на место прежнего — та же позиция в сетке,
  // та же обложка. Снимок черновика при этом становится своим файлом: на
  // сервере лежит прежний, а уйти должен новый.
  const replaceEditedPhoto = (item, editedFile) => {
    if (!item || !editedFile) return;
    if (item.source === "local") {
      const original = (Array.isArray(adImagesRef.current) ? adImagesRef.current : [])[item.localIndex];
      if (!original) return;
      setAdImages((prev) => (Array.isArray(prev) ? prev : []).map((file) => (file === original ? editedFile : file)));
      if (typeof setPhotoOrder === "function") {
        setPhotoOrder((prev) => (Array.isArray(prev) ? prev : []).map((token) => (
          token?.kind === "file" && token.file === original ? { kind: "file", file: editedFile } : token
        )));
      }
      return;
    }
    const url = String(item.url || "").trim();
    if (!url || typeof setDraftImageUrls !== "function") return;
    if (typeof setPhotoOrder === "function") {
      setPhotoOrder((prev) => buildPhotoSequence(prev, draftImageUrlsRef.current, adImagesRef.current).map((token) => (
        token.kind === "draft" && token.url === url ? { kind: "file", file: editedFile } : token
      )));
    }
    setDraftImageUrls((prev) => (Array.isArray(prev) ? prev : []).filter((entry) => String(entry || "").trim() !== url));
    setAdImages((prev) => [...(Array.isArray(prev) ? prev : []), editedFile]);
  };

  const removeAdImage = (indexToRemove) => {
    stopPhotoDrag();
    setAdImages((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.filter((_, idx) => idx !== indexToRemove);
    });
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const removeDraftImage = (indexToRemove) => {
    if (typeof setDraftImageUrls !== "function") return;
    setDraftImageUrls((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.filter((_, idx) => idx !== indexToRemove);
    });
  };

  // Общий порядок: черновые снимки и свои файлы (utils/photoOrder). Наборы
  // читаются из ref-ов, чтобы перестановка во время перетаскивания видела
  // последние, а не те, что были при нажатии.
  const draftImageUrlsRef = useRef(draftImageUrls);
  draftImageUrlsRef.current = draftImageUrls;
  const movePhoto = (fromIndex, toIndex) => {
    if (typeof setPhotoOrder !== "function") return;
    setPhotoOrder((prev) => moveInSequence(
      buildPhotoSequence(prev, draftImageUrlsRef.current, adImagesRef.current),
      fromIndex,
      toIndex
    ));
  };

  // Левая кнопка мыши. На сенсорном экране тянуть пальцем значило бы отнять у
  // окна прокрутку — там порядок меняют кнопки на фото.
  const startPhotoDrag = (event, index) => {
    if (event.pointerType && event.pointerType !== "mouse") return;
    if (event.button !== 0) return;
    if (event.target && typeof event.target.closest === "function" && event.target.closest("button")) return;
    event.preventDefault();
    photoDragRef.current = { active: false, pending: true, index, startX: event.clientX, startY: event.clientY };
  };

  const movePhotoDragTo = (targetIndex) => {
    const current = photoDragRef.current;
    if (!current.active) return;
    if (!Number.isInteger(targetIndex) || current.index === targetIndex) return;
    movePhoto(current.index, targetIndex);
    photoDragRef.current = { ...current, index: targetIndex };
    setPhotoDragIndex(targetIndex);
  };

  useEffect(() => {
    if (!isOpen) return;
    // Пока сохранённая ветка не восстановлена, пустой путь окна — не выбор
    // пользователя, и стирать им путь в newAd нельзя.
    if (!didRestorePath && !categoryPath.length) return;
    setNewAd((prev) => {
      const currentPath = Array.isArray(prev.categoryPath) ? prev.categoryPath : [];
      const nextPath = categoryPath;
      if (currentPath.length === nextPath.length && currentPath.every((item, idx) => String(item) === String(nextPath[idx]))) {
        return prev;
      }
      return { ...prev, categoryPath: [...nextPath] };
    });
  }, [isOpen, categoryPath, didRestorePath, setNewAd]);

  // Восстановление выбранной категории при открытии — по сохранённому пути, а
  // не по номеру: у подкатегорий без своего номера он указывает на родителя,
  // и путь вышел бы короче выбранного. Уровень, которого ещё нет в дереве,
  // догружается — эффект повторится, когда дерево пополнится.
  useEffect(() => {
    if (!isOpen) return;
    if (categoryPath.length) return;
    if (didRestorePath) return;
    const matchesStep = (cat, step) =>
      (cat.id !== undefined && cat.id !== null && String(cat.id) === step) ||
      (cat.url && String(cat.url) === step);
    const savedPath = (Array.isArray(newAd?.categoryPath) ? newAd.categoryPath : [])
      .map((item) => String(item || ""))
      .filter(Boolean);
    if (savedPath.length) {
      let level = localCategories;
      let parent = null;
      let complete = true;
      for (const step of savedPath) {
        const node = (Array.isArray(level) ? level : []).find((cat) => matchesStep(cat, step));
        if (!node) {
          complete = false;
          break;
        }
        parent = node;
        level = node.children;
      }
      if (complete) {
        setCategoryPath(savedPath);
        syncQuickCategoryQueryFromPath(savedPath);
        setDidRestorePath(true);
        return;
      }
      const levelLoaded = parent
        ? Boolean(parent.childrenLoaded || (Array.isArray(parent.children) && parent.children.length))
        : localCategories.length > 0;
      if (!levelLoaded) {
        if (parent) requestChildren(parent);
        return;
      }
      // Уровень загружен, а шага в нём нет (категорию переименовали или
      // убрали) — ниже ищем по номеру категории.
    }
    const target = newAd?.categoryId || newAd?.categoryUrl;
    if (!target) return;
    const restoredPath = buildPathToNode(localCategories, target);
    if (restoredPath.length) {
      setCategoryPath(restoredPath);
      syncQuickCategoryQueryFromPath(restoredPath);
      setDidRestorePath(true);
    }
  }, [isOpen, categoryPath.length, didRestorePath, newAd?.categoryId, newAd?.categoryUrl, newAd?.categoryPath, localCategories]);

  useEffect(() => {
    if (!isOpen) return;
    if (!categoryPath.length) return;
    const lastId = categoryPath[categoryPath.length - 1];
    const node = findNode(localCategories, lastId);
    if (node && (!node.children || node.children.length === 0)) {
      requestChildren(node);
    }
  }, [isOpen, newAd.accountId, categoryPath, localCategories]);

  useEffect(() => {
    if (!isOpen) return;
    if (!categoryPath.length) {
      adModalDebugLog("[AdModal] category path empty");
      return;
    }
    const current = getCurrentCategories();
    adModalDebugLog("[AdModal] category path", categoryPath.join(" > "), "current count", current.length);
  }, [isOpen, categoryPath, localCategories]);

  useEffect(() => {
    if (!isOpen) return;
    if (!categoryListRef.current) return;
    categoryListRef.current.scrollTop = 0;
  }, [isOpen, categoryPath]);

  const getCurrentCategories = () => {
    if (categoryPath.length === 0) return localCategories;

    let current = localCategories;
    let parent = null;
    for (const pathItem of categoryPath) {
      const found = current.find((cat) =>
        (cat.id && String(cat.id) === String(pathItem)) ||
        (cat.url && String(cat.url) === String(pathItem))
      );
      if (!found) {
        const fallback = findNode(localCategories, pathItem);
        return filterCategoryChildren(fallback?.children || [], fallback);
      }
      parent = found;
      current = filterCategoryChildren(found.children || [], found);
    }
    return current;
  };

  const getSelectedCategory = () => {
    if (categoryPath.length === 0) return null;
    const lastId = categoryPath[categoryPath.length - 1];
    return findNode(localCategories, lastId);
  };


  const updateNodeChildren = (nodes, targetValue, children) =>
    nodes.map((node) => {
      const matches = String(node.id) === String(targetValue) || String(node.url) === String(targetValue);
      if (matches) {
        return { ...node, children, childrenLoaded: true };
      }
      if (node.children?.length) {
        return { ...node, children: updateNodeChildren(node.children, targetValue, children) };
      }
      return node;
    });

  const requestChildren = async (node) => {
    const rawId = node?.id !== undefined && node?.id !== null ? String(node.id) : "";
    const rawUrl = node?.url ? String(node.url) : "";
    const idValue = rawId && isNumericCategoryId(rawId) ? rawId : "";
    const urlValue = idValue ? "" : (rawUrl || "");
    if (!idValue && !urlValue) return;
    const targetValue = getCategoryValue(node) || idValue || urlValue;
    if (!targetValue) return;
    if (node?.childrenLoaded && Array.isArray(node?.children)) {
      return node.children;
    }

    const pending = pendingChildrenRef.current.get(targetValue);
    if (pending) return pending;

    const task = (async () => {
      const params = new URLSearchParams();
      if (idValue) params.set("id", idValue);
      else params.set("url", urlValue);
      if (newAd?.accountId) params.set("accountId", newAd.accountId);
      try {
        const data = await apiFetchJson(`/api/categories/children?${params.toString()}`, {
          timeoutMs: 90000,
          retry: true,
          allowBaseFallback: true
        });
        const rawChildren = Array.isArray(data?.children) ? data.children : null;
        if (!Array.isArray(rawChildren)) return null;
        const filteredChildren = filterCategoryChildren(rawChildren, node);
        const count = Array.isArray(filteredChildren) ? filteredChildren.length : 0;
        const sample = Array.isArray(filteredChildren)
          ? filteredChildren.slice(0, 5).map((item) => item?.name || item?.id || item?.url)
          : [];
        adModalDebugLog("[AdModal] children response count", count, "target", targetValue, "sample", sample);
        setLocalCategories((prev) => {
          const next = updateNodeChildren(prev, targetValue, filteredChildren);
          const updated = findNode(next, targetValue);
          adModalDebugLog("[AdModal] children applied count", updated?.children?.length || 0, "target", targetValue);
          return next;
        });
        setCategoryChildrenError("");
        return filteredChildren;
      } catch (error) {
        const message = error?.message || t("Ошибка сети");
        adModalDebugError("[AdModal] children request failed", {
          target: targetValue,
          accountId: newAd?.accountId || "",
          message
        });
        setCategoryChildrenError(tf`Не удалось загрузить подкатегории: ${message}`);
        return null;
      }
    })();

    pendingChildrenRef.current.set(targetValue, task);
    try {
      return await task;
    } finally {
      pendingChildrenRef.current.delete(targetValue);
    }
  };

  // Город под полем PLZ. Индекс из пяти цифр спрашивается у сервера с
  // задержкой, чтобы набор «10115» не стоил четырёх запросов; ответ сервер
  // держит у себя, так что повторный тот же индекс отвечается сразу.
  const [plzPlace, setPlzPlace] = useState({ code: "", city: "", state: "" });
  useEffect(() => {
    const code = String(newAd.postalCode || "").trim();
    if (!/^\d{5}$/.test(code)) {
      setPlzPlace({ code: "", city: "", state: "" });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetchJson(`/api/plz/${code}`);
        if (cancelled) return;
        setPlzPlace({ code, city: String(data?.city || ""), state: String(data?.state || "") });
      } catch (_) {
        // Справочник недоступен — подписи просто не будет.
        if (!cancelled) setPlzPlace({ code, city: "", state: "" });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [newAd.postalCode]);

  // Населённые пункты индекса: у одного PLZ их бывает несколько (28217 —
  // четыре района Walle), и на Kleinanzeigen при подаче выбирают один из них.
  // Список — как в форме KL, от имени аккаунта; выбранный уходит при
  // публикации (locationId). Пункт один — выбирать нечего, поле не показано.
  const [plzLocations, setPlzLocations] = useState({ code: "", items: [], loaded: false });
  useEffect(() => {
    const code = String(newAd.postalCode || "").trim();
    const accountId = String(newAd.accountId || "").trim();
    if (!isOpen || !accountId || !/^\d{5}$/.test(code)) {
      setPlzLocations({ code: "", items: [], loaded: false });
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await apiFetchJson(`/api/accounts/${accountId}/plz-locations?zip=${code}`, { timeoutMs: 45000 });
        if (cancelled) return;
        const items = (Array.isArray(data?.locations) ? data.locations : [])
          .map((item) => ({ id: String(item?.id || ""), name: String(item?.name || "") }))
          .filter((item) => item.id && item.name);
        setPlzLocations({ code, items, loaded: true });
      } catch (_) {
        // Kleinanzeigen не ответил — поля не будет, публикатор выберет сам.
        if (!cancelled) setPlzLocations({ code, items: [], loaded: false });
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isOpen, newAd.accountId, newAd.postalCode]);
  // Выбор по умолчанию — первый пункт, как в форме KL; сохранённый в черновике
  // остаётся, если он есть в списке. Список пришёл и выбирать нечего — выбор
  // снимается.
  useEffect(() => {
    const code = String(newAd.postalCode || "").trim();
    if (!plzLocations.loaded || plzLocations.code !== code) return;
    const { items } = plzLocations;
    setNewAd((prev) => {
      if (String(prev?.postalCode || "").trim() !== code) return prev;
      if (items.length < 2) {
        return prev.locationId || prev.locationName ? { ...prev, locationId: "", locationName: "" } : prev;
      }
      const current = items.find((item) => item.id === String(prev.locationId || ""));
      if (current) return current.name === prev.locationName ? prev : { ...prev, locationName: current.name };
      return { ...prev, locationId: items[0].id, locationName: items[0].name };
    });
  }, [plzLocations, newAd.postalCode, setNewAd]);
  const fetchAccountPostalCode = async () => {
    if (!newAd?.accountId) {
      alert(t("Сначала выберите аккаунт"));
      return;
    }
    setLoadingPlz(true);
    try {
      // Обычно ответ за секунды (форма публикации одним запросом). Запас — на
      // редкий случай, когда сервер дочитывает PLZ браузером.
      const data = await apiFetchJson(`/api/accounts/${newAd.accountId}/plz`, { timeoutMs: 120000 });
      if (data?.success && data?.postalCode) {
        setNewAd((prev) => withPostalCode(prev, data.postalCode));
        // Kleinanzeigen не ответил, и подставлен сохранённый PLZ — об этом надо
        // сказать, иначе он выглядит свежим.
        if (data?.fromCache && data?.warning) {
          alert(t(data.warning));
        }
      } else {
        alert(data?.error || t("Не удалось получить PLZ аккаунта"));
      }
    } catch (error) {
      alert(error?.message || t("Не удалось получить PLZ аккаунта"));
    } finally {
      setLoadingPlz(false);
    }
  };

  const fetchProxyPostalCode = async () => {
    if (!newAd?.accountId) {
      alert(t("Сначала выберите аккаунт"));
      return;
    }
    setLoadingProxyPlz(true);
    try {
      const data = await apiFetchJson(`/api/accounts/${newAd.accountId}/proxy-plz?force=1`);
      if (data?.success && data?.postalCode) {
        setNewAd((prev) => withPostalCode(prev, data.postalCode));
        if (data?.warning) {
          alert(data.warning);
        }
      } else {
        alert(data?.error || t("Не удалось получить PLZ прокси"));
      }
    } catch (error) {
      alert(error?.message || t("Не удалось получить PLZ прокси"));
    } finally {
      setLoadingProxyPlz(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const accountId = String(newAd?.accountId || "").trim();
    if (!accountId) {
      lastAutofillAccountIdRef.current = "";
      return;
    }
    if (lastAutofillAccountIdRef.current === accountId) return;
    lastAutofillAccountIdRef.current = accountId;

    const selected = accounts.find((account) => String(account?.id || "") === accountId);
    if (!selected) return;
    const accountPostalCode = String(selected?.postalCode || "").trim();
    const proxyPostalCode = String(selected?.proxyPostalCode || "").trim();
    const nextPostalCode = accountPostalCode || proxyPostalCode || "";
    // PLZ аккаунта подставляется в пустое поле или на место подставленного
    // прежде (сменили аккаунт). Введённый руками — остаётся: раньше выбор
    // аккаунта его затирал, и вместе с ним пропадал выбор населённого пункта
    // (28217 → PLZ аккаунта, у которого пункт один).
    const currentPostalCode = String(newAd?.postalCode || "").trim();
    if (currentPostalCode && currentPostalCode !== autofilledPostalCodeRef.current) return;
    autofilledPostalCodeRef.current = nextPostalCode;
    setNewAd((prev) => {
      if (String(prev?.accountId || "").trim() !== accountId) return prev;
      return withPostalCode(prev, nextPostalCode);
    });
  }, [isOpen, accounts, newAd?.accountId, newAd?.postalCode, setNewAd]);

  useEffect(() => {
    if (!isOpen) return;
    if (categoryPath.length !== 1) return;
    const rootValue = String(categoryPath[0] || "");
    if (!rootValue) return;

    if (prefetchRootRef.current !== rootValue) {
      prefetchRootRef.current = rootValue;
      prefetchedTargetsRef.current = new Set();
      prefetchInFlightRef.current = false;
    }

    if (prefetchInFlightRef.current) return;
    const current = getCurrentCategories();
    if (!current.length) return;

    const token = ++prefetchTokenRef.current;
    prefetchInFlightRef.current = true;

    (async () => {
      for (const node of current) {
        if (prefetchTokenRef.current !== token) return;
        const key = getCategoryValue(node);
        if (!key) continue;
        if (prefetchedTargetsRef.current.has(key)) continue;
        prefetchedTargetsRef.current.add(key);
        if (node.children?.length || node.childrenLoaded) continue;
        await requestChildren(node);
      }
    })().finally(() => {
      if (prefetchTokenRef.current === token) {
        prefetchInFlightRef.current = false;
      }
    });
  }, [isOpen, newAd.accountId, categoryPath, localCategories]);

  const getCategoryBreadcrumb = () => {
    const breadcrumb = [];
    let current = localCategories;

    for (const pathItem of categoryPath) {
      const found = current.find((cat) =>
        (cat.id && String(cat.id) === String(pathItem)) ||
        (cat.url && String(cat.url) === String(pathItem))
      );
      if (found) {
        breadcrumb.push(found);
        current = filterCategoryChildren(found.children || [], found);
      }
    }

    return breadcrumb;
  };


  const breadcrumb = getCategoryBreadcrumb();
  const normalizedDraftImageUrls = useMemo(
    () => (
      Array.isArray(draftImageUrls)
        ? draftImageUrls.map((item) => String(item || "").trim()).filter(Boolean)
        : []
    ),
    [draftImageUrls]
  );
  // Сетка — в общем порядке (снимки черновика и свои вперемешку). Свой файл
  // показывается по превью; пока превью не готово (кадр после добавления),
  // плитки нет — она появится в следующей отрисовке.
  const combinedImageItems = useMemo(() => {
    const previewByFile = new Map(imagePreviews.map((preview) => [preview.file, preview.url]));
    const files = Array.isArray(adImages) ? adImages : [];
    return buildPhotoSequence(photoOrder, normalizedDraftImageUrls, files)
      .map((token) => {
        if (token.kind === "draft") {
          const draftIndex = normalizedDraftImageUrls.indexOf(token.url);
          return { key: `draft-${token.url}`, url: token.url, source: "draft", draftIndex };
        }
        const localIndex = files.indexOf(token.file);
        const url = previewByFile.get(token.file);
        if (!url) return null;
        return { key: `local-${url}`, url, source: "local", localIndex };
      })
      .filter(Boolean);
  }, [photoOrder, normalizedDraftImageUrls, imagePreviews, adImages]);
  const quickCategoryOptions = useMemo(
    () => buildQuickCategoryOptions(localCategories, []),
    [localCategories, categoryLangStamp]
  );
  const quickCategoryQueryNormalized = String(categoryQuickQuery || "").trim().toLowerCase();
  const REWRITE_LIMITS = { title: 65, description: 4000 };
  const rewriteReady = (field) => String(newAd?.[field] || "").trim().length >= (field === "title" ? 3 : 10);
  const setRewriteState = (field, patch) => setRewriteByField((prev) => ({ ...prev, [field]: { ...prev[field], ...patch } }));

  // Одно поле за нажатие. Второе поле уходит модели как контекст: название
  // переводится с оглядкой на описание и наоборот, но не переписывается.
  const requestRewrite = async (field) => {
    if (rewriteByField[field]?.status === "loading" || !rewriteReady(field)) return;
    const title = String(newAd?.title || "").trim();
    const description = String(newAd?.description || "").trim();
    setRewriteState(field, { status: "loading", error: "", backup: null });
    try {
      const data = await apiFetchJson("/api/ads/rewrite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ field, title, description }),
        timeoutMs: 90000
      });
      const next = String(data?.[field] || "").slice(0, REWRITE_LIMITS[field]);
      if (!next) throw new Error(t("Модель вернула пустой текст — повторите."));
      const truncatedFlag = field === "title" ? "_titleTruncated" : "_descTruncated";
      setNewAd((prev) => ({ ...prev, [field]: next, [truncatedFlag]: false }));
      setRewriteState(field, { status: "done", error: "", backup: newAd?.[field] || "", source: "rewrite" });
    } catch (error) {
      setRewriteState(field, { status: "error", error: error?.message || t("Не удалось переписать текст"), backup: null });
    }
  };

  const undoRewrite = (field) => {
    const backup = rewriteByField[field]?.backup;
    if (backup === null || backup === undefined) return;
    const truncatedFlag = field === "title" ? "_titleTruncated" : "_descTruncated";
    setNewAd((prev) => ({ ...prev, [field]: backup, [truncatedFlag]: false }));
    setRewriteState(field, { status: "idle", error: "", backup: null });
  };

  // Кнопка той же формы, что «Подобрать» у категории, и строка состояния рядом.
  const renderRewriteControl = (field, label = t("Уникализировать")) => {
    const state = rewriteByField[field] || {};
    const ready = rewriteReady(field);
    const busy = state.status === "loading";
    const doneLabel = state.source === "photos"
      ? (field === "title" ? t("Составлено по фото: название") : t("Составлено по фото: описание"))
      : (field === "title" ? t("Переписано на немецком: название") : t("Переписано на немецком: описание"));
    return (
      <div className="adf-ai-row">
        <button
          type="button"
          className="adf-chip"
          onClick={() => requestRewrite(field)}
          disabled={!ready || busy}
          title={t("Перевести на немецкий и переписать другими словами")}
        >
          {busy ? <LoadingIcon size={12} /> : <SparklesIcon size={12} />}
          {busy ? t("Переписываем...") : label}
        </button>
        {state.status === "done" && (
          <span className="adf-ai-note">
            {doneLabel}
            <button type="button" onClick={() => undoRewrite(field)}>
              {t("Вернуть как было")}
            </button>
          </span>
        )}
        {state.status === "error" && (
          <span className="adf-ai-note is-bad">{state.error}</span>
        )}
      </div>
    );
  };

  // Снимок ужимается до 1024px по длинной стороне и уходит как JPEG: модели
  // хватает, а тело запроса остаётся небольшим даже при восьми фото.
  const shrinkImageForModel = (source) => new Promise((resolve) => {
    const image = new Image();
    const url = source instanceof Blob ? URL.createObjectURL(source) : String(source || "");
    const cleanup = () => { if (source instanceof Blob) URL.revokeObjectURL(url); };
    image.onload = () => {
      try {
        const longest = Math.max(image.naturalWidth, image.naturalHeight) || 1;
        const scale = Math.min(1, 1024 / longest);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        cleanup();
        resolve({ mimeType: "image/jpeg", data: dataUrl.replace(/^data:[^;]+;base64,/, "") });
      } catch (_) { cleanup(); resolve(null); }
    };
    image.onerror = () => { cleanup(); resolve(null); };
    if (!(source instanceof Blob)) image.crossOrigin = "anonymous";
    image.src = url;
  });

  const requestPhotoDescribe = async () => {
    if (photoDescribe.status === "loading" || !combinedImageItems.length) return;
    setPhotoDescribe({ status: "loading", error: "", summary: "", notes: {} });
    try {
      const items = combinedImageItems.slice(0, 8);
      const shrunk = await Promise.all(items.map(async (item) => {
        if (item.source === "local") {
          const preview = imagePreviews[item.localIndex];
          return preview?.file ? shrinkImageForModel(preview.file) : null;
        }
        try {
          const response = await fetch(item.url);
          const blob = await response.blob();
          return shrinkImageForModel(blob);
        } catch (_) { return null; }
      }));
      const sent = [];
      const sentItems = [];
      shrunk.forEach((image, index) => {
        if (!image) return;
        sent.push(image);
        sentItems.push(items[index]);
      });
      if (!sent.length) throw new Error(t("Не удалось прочитать фото"));

      const data = await apiFetchJson("/api/ads/describe-photos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          images: sent,
          title: String(newAd?.title || "").trim(),
          description: String(newAd?.description || "").trim()
        }),
        timeoutMs: 120000
      });

      const done = [];
      const nextTitle = String(data?.title || "").slice(0, 65);
      const nextDescription = String(data?.description || "").slice(0, 4000);
      if (nextTitle) {
        setRewriteState("title", { status: "done", error: "", backup: newAd?.title || "", source: "photos" });
        done.push(t("название объявления"));
      }
      if (nextDescription) {
        setRewriteState("description", { status: "done", error: "", backup: newAd?.description || "", source: "photos" });
        done.push(t("текст описания"));
      }
      setNewAd((prev) => ({
        ...prev,
        ...(nextTitle ? { title: nextTitle, _titleTruncated: false } : {}),
        ...(nextDescription ? { description: nextDescription, _descTruncated: false } : {})
      }));
      if (data?.category?.id) {
        applyCategorySuggestion(data.category);
        done.push(t("категория"));
      }

      // Обложка: выбранный снимок становится первым — любой, и свой файл, и
      // снимок черновика (порядок теперь общий).
      const coverItem = sentItems[Number(data?.coverIndex) || 0];
      let coverNote = "";
      const coverPosition = coverItem ? combinedImageItems.indexOf(coverItem) : -1;
      if (coverPosition > 0) {
        movePhoto(coverPosition, 0);
        coverNote = tf`обложка — фото ${coverPosition + 1}`;
      }

      const notes = {};
      (Array.isArray(data?.photos) ? data.photos : []).forEach((entry) => {
        const item = sentItems[Number(entry?.index)];
        if (!item || !Array.isArray(entry?.labels) || !entry.labels.length) return;
        notes[item.key] = entry.labels;
      });
      const warnings = Object.keys(notes).length;
      setPhotoDescribe({
        status: "done",
        error: "",
        summary: [
          done.length ? tf`Готово: ${done.join(", ")}` : t("Модель ничего не предложила"),
          coverNote,
          warnings ? tf`замечаний к фото: ${warnings}` : ""
        ].filter(Boolean).join(" · "),
        notes
      });
    } catch (error) {
      setPhotoDescribe({ status: "error", error: error?.message || t("Не удалось описать фото"), summary: "", notes: {} });
    }
  };

  const requestCategorySuggestions = async () => {
    const title = String(newAd?.title || "").trim();
    if (title.length < 4) return;
    setCategorySuggest({ status: "loading", items: [], error: "", forTitle: title });
    try {
      const data = await apiFetchJson("/api/categories/suggest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title,
          description: String(newAd?.description || "").slice(0, 600)
        }),
        timeoutMs: 40000
      });
      const items = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setCategorySuggest({ status: items.length ? "done" : "error", items, error: items.length ? "" : t("Подходящая категория не нашлась"), forTitle: title });
    } catch (error) {
      // Настоящая причина показывается всегда. Раньше при автоматическом
      // запросе она глушилась, и обрыв связи выглядел так же, как «модель
      // ничего не подобрала», — то есть по полосе нельзя было понять, что
      // сломалось.
      setCategorySuggest({
        status: "error",
        items: [],
        error: error?.message || t("Не удалось подобрать категорию"),
        forTitle: title
      });
    }
  };

  // Подсказка запрашивается один раз на открытие списка и только под то
  // название, которое сейчас в форме: постоянный опрос на каждую букву стоил бы
  // обращения к модели на каждое нажатие клавиши.
  useEffect(() => {
    if (!categoryPickerOpen) return;
    const title = String(newAd?.title || "").trim();
    if (title.length < 4) return;
    if (categorySuggest.status === "loading") return;
    if (categorySuggest.forTitle === title && categorySuggest.status !== "idle") return;
    requestCategorySuggestions();
    // eslint-disable-next-line
  }, [categoryPickerOpen, newAd?.title]);

  useEffect(() => {
    if (categoryPickerOpen) setCategorySearch("");
  }, [categoryPickerOpen]);

  // Дерево у Kleinanzeigen в два уровня и целиком помещается на экран, поэтому
  // список плоский: раздел с подразделами сразу, без переходов по уровням. При
  // вводе он превращается в один список совпадений с полным путём у каждого.
  const categorySearchNormalized = String(categorySearch || "").trim().toLowerCase();
  const categorySearchMatches = useMemo(() => {
    if (categorySearchNormalized.length < 1) return [];
    const scored = [];
    for (const option of quickCategoryOptions) {
      const index = option.searchText.indexOf(categorySearchNormalized);
      if (index === -1) continue;
      const startsWith = option.label.toLowerCase().startsWith(categorySearchNormalized) ? 0 : 1;
      scored.push({ option, score: index + startsWith });
    }
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 60).map((item) => item.option);
  }, [quickCategoryOptions, categorySearchNormalized]);

  const applyCategorySuggestion = (suggestion) => {
    const pathValues = buildPathToNode(localCategories, suggestion?.id);
    if (!pathValues.length) return;
    const labels = pathValues.map((value) => {
      const node = findNode(localCategories, value);
      return node ? getCategoryLabel(node) : value;
    });
    handleQuickCategorySelect({
      value: String(suggestion.id),
      label: labels[labels.length - 1] || "",
      pathValues: pathValues.map((item) => String(item)),
      fullPathLabel: labels.join(" / ")
    });
    setCategoryPickerOpen(false);
  };

  const handleQuickCategorySelect = (option) => {
    if (!option || !Array.isArray(option.pathValues) || !option.pathValues.length) return;
    const nextPath = option.pathValues.map((item) => String(item));
    setDidRestorePath(true);
    setCategoryPath(nextPath);
    setCategoryChildrenError("");
    setCategoryQuickQuery(option.fullPathLabel || option.label || "");
    setCategoryQuickOpen(false);
    const { selectedNode, numericNode } = resolveCategorySelection(nextPath);
    setNewAd((prev) => {
      const next = {
        ...prev,
        categoryId: numericNode?.id ? String(numericNode.id) : "",
        categoryUrl: selectedNode?.url || numericNode?.url || "",
        categoryKey: nextPath.join(">"),
        categoryPath: [...nextPath]
      };
      if (!next.categoryId || next.categoryId !== prev.categoryId) {
        if (setExtraFieldValues) {
          setExtraFieldValues({});
        }
      }
      return next;
    });
  };
  // Категория (design 2a §7): collapsed to a single row — path above, leaf below.
  // The full tree stays exactly as it is and opens on click.
  const categoryLabels = breadcrumb.map((item) => getCategoryLabel(item)).filter(Boolean);
  const categoryLeafLabel = categoryLabels.length
    ? categoryLabels[categoryLabels.length - 1]
    : (newAd?.categoryId || newAd?.categoryUrl ? t("Категория выбрана") : t("Не выбрана"));

  // Полный путь рубрики для предпросмотра: «Elektronik › Audio & Hifi › …».
  const previewCategoryPath = (() => {
    const labels = breadcrumb.map((item) => getCategoryLabel(item)).filter(Boolean);
    if (labels.length) return labels.join(" › ");
    if (newAd?.categoryId || newAd?.categoryUrl) return t("Категория выбрана");
    return "";
  })();

  // Название — заголовком карточки-«документа», одной строкой без переносов.
  const handleTitleChange = (e) => {
    const val = e.target.value.replace(/\n+/g, " ");
    if (val.length <= 65) {
      setNewAd((prev) => ({ ...prev, title: val, _titleTruncated: false }));
    } else {
      setNewAd((prev) => ({ ...prev, title: val.slice(0, 65), _titleTruncated: true }));
    }
  };
  const handleTitlePaste = (e) => {
    const paste = (e.clipboardData?.getData("text") || "").replace(/\n+/g, " ");
    const current = newAd.title || "";
    const sel = e.target.selectionStart ?? current.length;
    const selEnd = e.target.selectionEnd ?? sel;
    const result = current.slice(0, sel) + paste + current.slice(selEnd);
    if (result.length > 65) {
      e.preventDefault();
      setNewAd((prev) => ({ ...prev, title: result.slice(0, 65), _titleTruncated: true }));
    }
  };

  const postalCodeValue = String(newAd?.postalCode || "").trim();
  const localityOptions = plzLocations.code === postalCodeValue ? plzLocations.items : [];
  const sellerName = selectedAccountMeta
    ? String(selectedAccountMeta.profileName || selectedAccountMeta.username || t("Аккаунт")).trim()
    : "";

  // Готовность — шесть пунктов, как их проверяет публикация: фото, название,
  // описание, цена, категория с обязательными параметрами, продавец.
  const readinessSteps = [
    combinedImageItems.length > 0,
    String(newAd?.title || "").trim().length > 0,
    String(newAd?.description || "").trim().length > 0,
    String(newAd?.price || "").trim().length > 0,
    hasSelectedCategory && !loadingExtraFields && !extraFieldsError && missingRequiredExtraFields.length === 0,
    Boolean(newAd?.accountId)
  ];
  const readyCount = readinessSteps.filter(Boolean).length;
  const readyShare = readyCount / readinessSteps.length;

  // Строки справа раскрываются по одной: продавец, категория, место.
  const openSideRow = (row) => {
    setAccountDropdownOpen(row === "seller" ? !accountDropdownOpen : false);
    setCategoryPickerOpen(row === "category" ? !categoryPickerOpen : false);
    setLocationOpen(row === "location" ? !locationOpen : false);
  };
  const placeName = localityOptions.length > 1
    ? (String(newAd?.locationName || "").trim() || localityOptions[0]?.name || "")
    : (plzPlace.code === postalCodeValue ? plzPlace.city : "");
  const localityWord = (n) => ((n % 10 === 1 && n % 100 !== 11)
    ? t("пункт")
    : ((n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20)) ? t("пункта") : t("пунктов")));

  // Горячие клавиши работают, пока не открыт редактор фото или предпросмотр —
  // у тех свои кнопки.
  hotkeyActionsRef.current = {
    next: () => {
      if (photoEditorItem || showPreview || publishing) return;
      handlePublishClick();
    },
    save: () => {
      if (photoEditorItem || showPreview || publishing) return;
      onSaveDraft?.();
    },
    addFiles: (files) => {
      if (photoEditorItem) return;
      appendAdImageFiles(files);
    }
  };
  // Фото можно бросить на любое место окна: поверх появляется подсказка.
  const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");
  const modalDropProps = {
    onDragEnter: (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
      fileDragDepthRef.current += 1;
      if (!filesOverModal) setFilesOverModal(true);
    },
    onDragOver: (event) => {
      if (!hasDraggedFiles(event)) return;
      event.preventDefault();
    },
    onDragLeave: (event) => {
      if (!hasDraggedFiles(event)) return;
      fileDragDepthRef.current = Math.max(0, fileDragDepthRef.current - 1);
      if (!fileDragDepthRef.current) setFilesOverModal(false);
    },
    onDrop: (event) => {
      if (!hasDraggedFiles(event)) return;
      fileDragDepthRef.current = 0;
      setFilesOverModal(false);
      handleImageDrop(event);
    }
  };

  // Фото — лентой одинаковых плиток: обложка первой, с рамкой акцента, последней —
  // «Добавить». Порядок — перетаскиванием (data-photo-index).
  const removePhotoItem = (item) => {
    if (!item) return;
    if (item.source === "local") removeAdImage(item.localIndex);
    else removeDraftImage(item.draftIndex);
  };
  const renderPhotos = () => (
    <div className="nf-card nf-photos">
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleImageInputChange}
        style={{ display: "none" }}
      />
      {combinedImageItems.length ? (
        <div className="nf-mosaic">
          {combinedImageItems.map((item, index) => {
            const dragging = photoDragIndex === index;
            const notes = Array.isArray(photoDescribe.notes[item.key]) ? photoDescribe.notes[item.key] : [];
            return (
              <div
                key={item.key}
                data-photo-index={index}
                className={`nf-tile${index === 0 ? " is-cover" : ""}${dragging ? " is-dragging" : ""}`}
                onPointerDown={(event) => startPhotoDrag(event, index)}
                style={{ cursor: photoDragActive ? "grabbing" : "grab" }}
                title={index === 0 ? t("Обложка") : undefined}
              >
                <img src={item.url} alt="" draggable={false} />
                {index === 0 ? (
                  <span className="nf-badge"><StarIcon size={10} fill="currentColor" /> {t("Обложка")}</span>
                ) : (
                  <span className="nf-num">{index + 1}</span>
                )}
                {notes.length > 0 && <span className="nf-tile-note" title={notes.join(", ")}>{notes[0]}</span>}
                <span className="nf-tile-tools">
                  {index > 0 && (
                    <button type="button" onClick={(event) => { event.stopPropagation(); movePhoto(index, 0); }} title={t("Сделать обложкой")} aria-label={t("Сделать обложкой")}>
                      <StarIcon size={12} />
                    </button>
                  )}
                  <button type="button" onClick={(event) => { event.stopPropagation(); stopPhotoDrag(); setPhotoEditorItem(item); }} title={t("Редактировать фото")} aria-label={t("Редактировать фото")}>
                    <CropIcon size={12} />
                  </button>
                  <button type="button" className="is-danger" onClick={(event) => { event.stopPropagation(); removePhotoItem(item); }} title={t("Удалить фото")} aria-label={t("Удалить фото")}>
                    <XIcon size={12} />
                  </button>
                </span>
              </div>
            );
          })}
          {combinedImageItems.length < 20 && (
            <button type="button" className="nf-add" onClick={() => imageInputRef.current?.click()} title={t("Добавить фото")}>
              <span className="nf-add-ico"><PlusIcon size={16} /></span>
              <span>{t("Добавить")}</span>
            </button>
          )}
        </div>
      ) : (
        <button type="button" className="nf-drop" onClick={() => imageInputRef.current?.click()}>
          <span className="nf-drop-ico"><UploadIcon size={20} /></span>
          <span className="nf-drop-text">
            <span className="nf-drop-title">{t("Добавьте фото")}</span>
            <span className="nf-drop-sub">
              {isPhoneLayout ? t("До 20 снимков · первый станет обложкой") : t("Перетащите в окно или нажмите, чтобы выбрать · до 20 снимков, первый станет обложкой")}
            </span>
          </span>
        </button>
      )}
      <div className="nf-photos-foot">
        <span>
          {combinedImageItems.length} {t("из 20")}{combinedImageItems.length > 1 && !isPhoneLayout ? t(" · перетащите, чтобы поменять порядок") : ""}
        </span>
        {aiPrefs.aiPhotoDescribe && (
          <button
            type="button"
            className="adf-chip"
            onClick={requestPhotoDescribe}
            disabled={!combinedImageItems.length || photoDescribe.status === "loading"}
            title={t("Модель посмотрит фото и напишет название, описание, подберёт категорию и обложку")}
          >
            {photoDescribe.status === "loading" ? <LoadingIcon size={12} /> : <SparklesIcon size={12} />}
            {photoDescribe.status === "loading" ? t("Смотрим фото...") : t("Описать по фото")}
          </button>
        )}
      </div>
      {(photoDescribe.status === "done" || photoDescribe.status === "error") && (
        <div className={`adf-ai-note${photoDescribe.status === "error" ? " is-bad" : ""}`}>
          {photoDescribe.status === "error" ? photoDescribe.error : photoDescribe.summary}
        </div>
      )}
    </div>
  );

  return (
    <div ref={overlayRef} className="modal-overlay ad-modal-root" {...overlayMotionProps(dialogState)} style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 15% 10%, ${aa(800, 0.24)}, rgba(8,9,12,0.92) 46%, rgba(8,9,12,0.98) 100%)`,
      backdropFilter: "blur(10px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      // Above the mobile bottom tab bar (z 9990) so it can't cover the modal's
      // footer buttons; extra bottom padding keeps them off the safe-area edge.
      zIndex: 12000,
      // Сверху — с запасом под вырез: в веб-приложении на iPhone страница идёт
      // под строку состояния, и крестик оказывался под часами, где его не нажать.
      padding: isPhoneLayout
        ? "calc(14px + env(safe-area-inset-top, 0px)) 12px calc(14px + env(safe-area-inset-bottom, 0px))"
        : "20px"
    }}>
      <div className={`modal-card modal-card-wide modal-card-tall ad-modal-card adf-shell nf${isPhoneLayout ? " is-phone" : ""}`} data-modal-scroll="1" {...dialogMotionProps(dialogState)} style={{
        padding: isPhoneLayout ? "0 12px" : 0,
        width: isPhoneLayout ? "720px" : "1180px",
        maxWidth: "100%",
        height: isPhoneLayout ? "auto" : "860px",
        // На телефоне — по высоте окна за вычетом отступов, а не 90vh: в
        // веб-приложении на iPhone vh бывает больше видимой области (после
        // клавиатуры iOS укорачивает страницу), и карточка вылезала под часы.
        maxHeight: isPhoneLayout ? "100%" : "92vh",
        // На телефоне прокручивается всё окно, кнопка публикации липнет книзу.
        // На столе прокручиваются колонки, шапка стоит.
        overflowY: isPhoneLayout ? "auto" : "hidden",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        display: "flex",
        flexDirection: "column"
      }} {...modalDropProps}>
        {filesOverModal && (
          <div className="nf-dropzone">
            <span className="nf-dropzone-ico"><UploadIcon size={26} /></span>
            <b>{t("Отпустите — фото добавятся в объявление")}</b>
            <span>{t("До 20 снимков, первый станет обложкой")}</span>
          </div>
        )}
        {/* Шапка: название и готовность, импорт лота по ссылке, крестик. */}
        <div className="nf-head">
          <div className="nf-head-top">
            <div className="nf-title">
              <h2>{t("Новое объявление")}</h2>
              <span className="nf-progress">
                <span className="nf-progress-bar"><span style={{ width: `${Math.round(readyShare * 100)}%` }} /></span>
                {tf`Готово ${readyCount} из ${readinessSteps.length}`}
              </span>
            </div>
            {isPhoneLayout && (
              <button type="button" className="ui-icon-btn nf-close" onClick={onClose} aria-label={t("Закрыть")} title={t("Закрыть")}>
                <XIcon size={18} />
              </button>
            )}
          </div>
          <div className="nf-import-wrap">
            <div className="nf-import">
              {/* Площадка — родной <select>: открывается поверх окна без забот о
                  слоях и на телефоне работает как привычный выбор системы. */}
              <span className="nf-site">
                <select
                  value={grabSiteId}
                  disabled={ebayImporting}
                  onChange={(event) => {
                    setGrabSiteId(event.target.value);
                    if (ebayImportError) setEbayImportError("");
                  }}
                  title={t("Площадка, с которой забирать объявление")}
                >
                  {GRAB_SITES.map((site) => (
                    <option key={site.id} value={site.id} style={{ background: S.elevated, color: "#e2e4e9" }}>
                      {site.label}
                    </option>
                  ))}
                </select>
                <ChevronRightIcon size={11} />
              </span>
              <span className="nf-import-link"><LinkIcon size={15} /></span>
              <input
                type="url"
                className="nf-import-input"
                value={ebayUrl}
                disabled={ebayImporting}
                onChange={(e) => {
                  setEbayUrl(e.target.value);
                  if (ebayImportError) setEbayImportError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !ebayImporting) {
                    event.preventDefault();
                    handleEbayGrab();
                  }
                }}
                placeholder={isPhoneLayout ? t("Ссылка на лот") : t("Вставьте ссылку на лот — заберём название, описание и фото")}
              />
              <button type="button" className="ui-btn-primary" onClick={handleEbayGrab} disabled={ebayImporting}>
                {ebayImporting ? (<><LoadingIcon size={13} /> {t("Импорт...")}</>) : t("Забрать")}
              </button>
            </div>
            {(ebayImportStatus || ebayImportError) && (
              <div className={`nf-import-note${ebayImportError ? " is-bad" : ""}`}>{ebayImportError || ebayImportStatus}</div>
            )}
          </div>
          {!isPhoneLayout && (
            <button type="button" className="ui-icon-btn nf-close" onClick={onClose} aria-label={t("Закрыть")} title={t("Закрыть")}>
              <XIcon size={18} />
            </button>
          )}
        </div>

        <div ref={bodyScrollRef} className="nf-body">
          <div className="nf-col nf-col--main">
            {renderPhotos()}

            {/* Название и текст — одной карточкой, как документ: поля не
                выглядят полями, пока по ним не кликнешь. */}
            <div className="nf-card nf-doc">
              <textarea
                ref={titleFieldRef}
                className="nf-plain nf-doc-title"
                value={newAd.title}
                maxLength={65}
                rows={1}
                onChange={handleTitleChange}
                onPaste={handleTitlePaste}
                onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                placeholder={t("Как называется товар?")}
                aria-label={t("Название")}
              />
              <div className="nf-doc-meta">
                <span className="nf-doc-bar">
                  <span style={{ width: `${Math.min(100, Math.round((titleLength / 65) * 100))}%` }} className={titleLength >= 65 ? "is-bad" : (titleLength >= 60 ? "is-warn" : "")} />
                </span>
                <span className={`adf-meta${titleLength >= 65 ? " is-bad" : (titleLength >= 60 ? " is-warn" : "")}`}>{titleLength} / 65</span>
              </div>
              {renderRewriteControl("title", t("Уникализировать название"))}
              <div className="nf-doc-sep" />
              <textarea
                ref={descriptionFieldRef}
                className={`nf-plain nf-doc-text${descriptionLength >= 4000 ? " is-over" : ""}`}
                value={newAd.description}
                maxLength={4000}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 4000) {
                    setNewAd((prev) => ({ ...prev, description: val, _descTruncated: false }));
                  } else {
                    setNewAd((prev) => ({ ...prev, description: val.slice(0, 4000), _descTruncated: true }));
                  }
                }}
                onPaste={(e) => {
                  const paste = e.clipboardData?.getData("text") || "";
                  const current = newAd.description || "";
                  const sel = e.target.selectionStart ?? current.length;
                  const selEnd = e.target.selectionEnd ?? sel;
                  const result = current.slice(0, sel) + paste + current.slice(selEnd);
                  if (result.length > 4000) {
                    e.preventDefault();
                    setNewAd((prev) => ({ ...prev, description: result.slice(0, 4000), _descTruncated: true }));
                  }
                }}
                placeholder={t("Расскажите о товаре: состояние, комплект, причина продажи…")}
                aria-label={t("Описание")}
              />
              <div className="nf-doc-tools">
                {renderRewriteControl("description", t("Уникализировать текст"))}
                <span className={`adf-meta nf-doc-count${descriptionLength >= 4000 ? " is-bad" : ""}`}>
                  {newAd._descTruncated ? `${t("Текст обрезан до 4000 символов")} · ` : ""}{descriptionLength} / 4000
                </span>
              </div>
            </div>
          </div>

          <div className="nf-col nf-col--side">
            <div className="nf-side-scroll">
              {/* Цена — крупно, отдельной карточкой. */}
              <div className="nf-card nf-price">
                <div className="nf-price-top">
                  <span className="nf-label">{t("Цена")}</span>
                  <div className="adf-seg" role="radiogroup" aria-label={t("Тип цены")}>
                    {[{ value: "FIXED", label: "Festpreis" }, { value: "NEGOTIABLE", label: "VB" }].map((opt) => {
                      const active = String(newAd.priceType || "FIXED") === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          className={`adf-seg-btn${active ? " is-on" : ""}`}
                          onClick={() => setNewAd((prev) => ({ ...prev, priceType: opt.value }))}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <label className="nf-price-val">
                  <input
                    type="text"
                    inputMode="decimal"
                    className="nf-plain nf-price-input"
                    value={newAd.price}
                    onChange={(e) => setNewAd((prev) => ({ ...prev, price: normalizePriceInput(e.target.value) }))}
                    onWheel={(e) => e.currentTarget.blur()}
                    placeholder="0"
                    aria-label={t("Цена")}
                    // Ширина — по числу: знак € стоит сразу за суммой.
                    style={{ width: `calc(${Math.max(1, String(newAd.price || "").length) * 0.62}em + 12px)` }}
                  />
                  <small>€</small>
                </label>
              </div>

              {/* Продавец, категория, место — строками; по нажатию строка
                  раскрывается. */}
              <div className="nf-card nf-list">
                <div ref={accountDropdownRef} className={`ui-select nf-select${accountDropdownOpen ? " is-open" : ""}`}>
                  <button
                    type="button"
                    className={`nf-row${accountDropdownOpen ? " is-open" : ""}`}
                    aria-haspopup="listbox"
                    aria-expanded={accountDropdownOpen}
                    onClick={() => openSideRow("seller")}
                  >
                    <span className="nf-ico is-avatar">{sellerName ? sellerName.charAt(0).toUpperCase() : <UserIcon size={18} />}</span>
                    <span className="nf-row-text">
                      <span className="nf-row-label">{t("Продавец")}</span>
                      {selectedAccountMeta ? (
                        <span className="nf-row-value">{sellerName} <small>· {getAccountProxyLabel(selectedAccountMeta)}</small></span>
                      ) : (
                        <span className="nf-row-value is-empty">{t("Выберите аккаунт")}</span>
                      )}
                    </span>
                    {selectedAccountMeta && <SatisfactionBadge value={normalizeSatisfactionBadge(selectedAccountMeta.satisfactionBadge)} />}
                    <ChevronRightIcon size={16} className="nf-chev" />
                  </button>
              {accountDropdownOpen && (
                <div className="ui-select-menu" role="listbox" data-modal-scroll="1">
                  <button
                    type="button"
                    role="option"
                    aria-selected={!newAd.accountId}
                    className={`ui-select-option ui-select-option--hover${!newAd.accountId ? " is-selected" : ""}`}
                    onClick={() => {
                      setNewAd((prev) => ({ ...prev, accountId: "" }));
                      setAccountDropdownOpen(false);
                    }}
                  >
                    <span className="ui-select-option-text"><span className="ui-select-option-label">{t("Без аккаунта")}</span></span>
                    {!newAd.accountId && <span className="ui-select-check"><CheckIcon size={15} /></span>}
                  </button>
                  {accounts.map((account) => {
                    const isSelected = String(account.id) === String(newAd.accountId);
                    const badge = normalizeSatisfactionBadge(account.satisfactionBadge);
                    return (
                      <button
                        key={account.id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`ui-select-option ui-select-option--hover${isSelected ? " is-selected" : ""}`}
                        onClick={() => {
                          setNewAd((prev) => ({ ...prev, accountId: String(account.id) }));
                          setAccountDropdownOpen(false);
                        }}
                      >
                        <span className="ui-select-option-text">
                          <span className="ui-select-option-label">{formatAccountLabel(account)}</span>
                          <span className="ui-select-option-sub">{getAccountProxyLabel(account)}</span>
                        </span>
                        <SatisfactionBadge value={badge} />
                        <span className="ui-select-check" style={{ visibility: isSelected ? "visible" : "hidden" }}><CheckIcon size={15} /></span>
                      </button>
                    );
                  })}
                </div>
              )}
                </div>

                <button
                  type="button"
                  className={`nf-row${categoryPickerOpen ? " is-open" : ""}`}
                  onClick={() => openSideRow("category")}
                >
                  <span className="nf-ico">
                    {React.createElement(hasSelectedCategory ? categoryIconFor(categoryLeafLabel, categoryLabels.join(" ")) : PackageGlyph, { size: 18, strokeWidth: 1.9 })}
                  </span>
                  <span className="nf-row-text">
                    <span className="nf-row-label">{t("Категория")}</span>
                    {hasSelectedCategory ? (
                      <span className="nf-row-value">
                        {categoryLeafLabel}
                        {categoryLabels.length > 1 && <small> · {categoryLabels.slice(0, -1).join(" › ")}</small>}
                      </span>
                    ) : (
                      <span className="nf-row-value is-empty">{t("Выберите категорию")}</span>
                    )}
                  </span>
                  <ChevronRightIcon size={16} className="nf-chev" />
                </button>
                {categoryPickerOpen && (
                  <div className="nf-expand">
                <div className="adf-pop">
                  {/* Поиск по всему дереву: уровней у Kleinanzeigen два, всего около
                      полутора сотен категорий — искать быстрее, чем ходить по ним. */}
                  <div className="adf-pop-search">
                    <div style={{ position: "relative" }}>
                      <span className="adf-pop-search-ico"><SearchIcon size={15} /></span>
                      <input
                        type="text"
                        className="adf-input"
                        autoFocus
                        value={categorySearch}
                        onChange={(event) => setCategorySearch(event.target.value)}
                        placeholder={t("Поиск категории")}
                      />
                      {categorySearch && (
                        <button
                          type="button"
                          className="ui-icon-btn adf-pop-clear"
                          onClick={() => setCategorySearch("")}
                          title={t("Очистить")}
                        >
                          <XIcon size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Подсказка модели по названию объявления. */}
                  {String(newAd?.title || "").trim().length >= 4 && (
                    <div className="adf-pop-suggest">
                      <div className="adf-pop-suggest-head">
                        <span className="adf-pop-suggest-title">
                          <SparklesIcon size={13} />
                          {categorySuggest.status === "loading" ? t("Подбираем...") : t("Похоже на")}
                        </span>
                        {categorySuggest.status === "loading" && (
                          <span className="adf-pop-suggest-note">{t("по названию объявления")}</span>
                        )}
                        {categorySuggest.status === "idle" && (
                          <span className="adf-pop-suggest-note">{t("нажмите «Подобрать»")}</span>
                        )}
                        {categorySuggest.status === "error" && (
                          <span className="adf-pop-suggest-note">{categorySuggest.error || t("Подходящая категория не нашлась")}</span>
                        )}
                        {categorySuggest.status !== "loading" && (
                          <button
                            type="button"
                            className="adf-chip"
                            style={{ marginLeft: "auto" }}
                            onClick={() => requestCategorySuggestions()}
                          >
                            {categorySuggest.status === "idle" ? t("Подобрать") : t("Подобрать заново")}
                          </button>
                        )}
                      </div>
                      {categorySuggest.status === "done" && categorySuggest.items.map((item, index) => {
                        const parts = String(item.path || "").split(">").map((part) => catText(part.trim())).filter(Boolean);
                        const name = parts[parts.length - 1] || "";
                        const trail = parts.slice(0, -1).join(" › ");
                        return (
                          <button
                            key={item.id}
                            type="button"
                            className={`adf-pop-item is-suggest${index === 0 ? " is-top" : ""}`}
                            onClick={() => applyCategorySuggestion(item)}
                            title={t("Выбрать эту категорию")}
                          >
                            <span className="adf-pop-item-trail">{trail || t("Раздел")}</span>
                            <span className="adf-pop-item-name">{name}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div ref={categoryListRef} className="adf-pop-list">
                    {categorySearchNormalized ? (
                      categorySearchMatches.length ? (
                        categorySearchMatches.map((option) => {
                          const isSelected = String(newAd?.categoryId || "") === String(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              className={`adf-pop-item${isSelected ? " is-selected" : ""}`}
                              onClick={() => { handleQuickCategorySelect(option); setCategoryPickerOpen(false); }}
                            >
                              <span className="adf-pop-item-trail">
                                {option.fullPathLabel.split(" / ").slice(0, -1).join(" › ") || t("Раздел")}
                              </span>
                              <span className="adf-pop-item-name">{option.label}</span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="adf-pop-empty">{t("Категории не найдены")}</div>
                      )
                    ) : (
                      localCategories.map((section) => {
                        const sectionLabel = getCategoryLabel(section);
                        const children = Array.isArray(section.children) ? section.children : [];
                        const sectionValue = getCategoryValue(section);
                        return (
                          <div key={sectionValue || sectionLabel} style={{ marginBottom: "4px" }}>
                            <div className="adf-pop-group">{sectionLabel}</div>
                            {(children.length ? children : [section]).map((node) => {
                              const value = getCategoryValue(node);
                              const isSelected = String(newAd?.categoryId || "") === String(value);
                              return (
                                <button
                                  key={value || getCategoryLabel(node)}
                                  type="button"
                                  className={`adf-pop-item${isSelected ? " is-selected" : ""}`}
                                  onClick={() => {
                                    handleQuickCategorySelect({
                                      value: String(value),
                                      label: getCategoryLabel(node),
                                      pathValues: children.length ? [String(sectionValue), String(value)] : [String(value)],
                                      fullPathLabel: children.length
                                        ? `${sectionLabel} / ${getCategoryLabel(node)}`
                                        : sectionLabel
                                    });
                                    setCategoryPickerOpen(false);
                                  }}
                                >
                                  {getCategoryLabel(node)}
                                </button>
                              );
                            })}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {categoryChildrenError && <div className="adf-pop-error">{categoryChildrenError}</div>}
                </div>
                  </div>
                )}

                <button
                  type="button"
                  className={`nf-row${locationOpen ? " is-open" : ""}`}
                  onClick={() => openSideRow("location")}
                >
                  <span className="nf-ico"><MapPinIcon size={18} /></span>
                  <span className="nf-row-text">
                    <span className="nf-row-label">{t("Местоположение")}</span>
                    {postalCodeValue ? (
                      <span className="nf-row-value">{postalCodeValue}{placeName ? <small> · {placeName}</small> : null}</span>
                    ) : (
                      <span className="nf-row-value is-empty">{t("Укажите PLZ")}</span>
                    )}
                  </span>
                  {localityOptions.length > 1 && (
                    <span className="nf-row-badge" title={t("У этого PLZ несколько населённых пунктов")}>
                      {localityOptions.length} {localityWord(localityOptions.length)}
                    </span>
                  )}
                  <ChevronRightIcon size={16} className="nf-chev" />
                </button>
                {locationOpen && (
                  <div className="nf-expand nf-expand--wide">
                    <div className="nf-plz-row">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="adf-input nf-plz"
                        value={newAd.postalCode}
                        onChange={(e) => { const value = e.target.value; setNewAd((prev) => withPostalCode(prev, value)); }}
                        placeholder="PLZ"
                        aria-label="PLZ"
                        autoFocus={!postalCodeValue}
                      />
                      {/* Подставить PLZ — как в остальной панели: короткая подпись
                          со значком, полная фраза — в подсказке. */}
                      {[
                        { key: "account", label: t("Аккаунт"), hint: t("Подставить PLZ аккаунта"), Icon: UserIcon, onClick: fetchAccountPostalCode, busy: loadingPlz },
                        { key: "proxy", label: t("Прокси"), hint: t("Подставить PLZ прокси"), Icon: LinkIcon, onClick: fetchProxyPostalCode, busy: loadingProxyPlz }
                      ].map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          className="plz-fill-btn"
                          onClick={item.onClick}
                          disabled={item.busy}
                          title={item.hint}
                          aria-label={item.hint}
                        >
                          {item.busy ? <LoadingIcon size={15} /> : <item.Icon size={15} />}
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                    {localityOptions.length > 1 ? (
                      <div className="adf-param plz-location-pick">
                        <span className="adf-sublabel">{t("Населённый пункт")} · {tf`${localityOptions.length} у этого PLZ`}</span>
                        <StyledSelect
                          value={String(newAd.locationId || "")}
                          onChange={(nextId) => {
                            const picked = localityOptions.find((item) => item.id === String(nextId));
                            setNewAd((prev) => ({ ...prev, locationId: String(nextId || ""), locationName: picked?.name || "" }));
                          }}
                          options={localityOptions.map((item) => ({ value: item.id, label: item.name }))}
                          placeholder={t("Выберите")}
                          ariaLabel={t("Населённый пункт")}
                        />
                      </div>
                    ) : null}
                    {!newAd.accountId && /^\d{5}$/.test(postalCodeValue) && (
                      <div className="nf-hint">{t("Выберите продавца — у одного PLZ бывает несколько населённых пунктов, список берётся с его аккаунта.")}</div>
                    )}
                  </div>
                )}
              </div>

              {hasSelectedCategory && (
                <div className="nf-card nf-params">
                  <div className="nf-params-head">
                    <span>{t("Параметры категории")}</span>
                    {missingRequiredExtraFields.length > 0 && (
                      <em>{tf`обязательных: ${missingRequiredExtraFields.length}`}</em>
                    )}
                  </div>
                  {loadingExtraFields ? (
                    <div className="nf-hint"><LoadingIcon size={13} /> {t("Загрузка параметров категории...")}</div>
                  ) : extraFieldsError ? (
                    <div className="nf-hint is-bad">{t("Не удалось загрузить параметры категории:")} {extraFieldsError}</div>
                  ) : visibleExtraFields.length > 0 ? (
                    <div className="nf-pgrid">
                {visibleExtraFields.map((field) => {
                  const key = field.name || field.label;
                  const value = key ? (extraFieldValues?.[key] || "") : "";
                  const isMissingRequired = Boolean(field.required) && !String(value ?? "").trim();
                  const fieldOptions = resolveExtraFieldOptions(field);
                  const fieldTypeToken = String(field.type || "").toUpperCase();
                  const fieldInputType = fieldTypeToken === "DATE"
                    ? "month"
                    : (["LONG", "INTEGER", "DECIMAL"].includes(fieldTypeToken) ? "number" : "text");
                  return (
                    <div
                      key={field.name || field.label}
                      data-missing-required={isMissingRequired ? "1" : undefined}
                      className="adf-param"
                    >
                      {/* Незаполненное обязательное — подпись янтарём, поле обычное. */}
                      <span className={`adf-sublabel${isMissingRequired ? " is-missing" : ""}`}>
                        {isMissingRequired ? (<><span style={{ fontWeight: 700 }}>{catText(field.label)}</span> {t("· обязательно")}</>) : catText(field.label)}
                      </span>
                      {fieldOptions.length > 0 ? (
                        <StyledSelect
                          value={value}
                          onChange={(nextValue) => setExtraFieldValues((prev) => {
                            const next = { ...prev, [key]: nextValue };
                            // Сменили родительский параметр — прежний выбор в
                            // зависимом от него поле больше не годится: у нового
                            // значения свой список, а то и нет поля вовсе.
                            (Array.isArray(extraFields) ? extraFields : []).forEach((other) => {
                              if (other?.dependsOn !== key) return;
                              const otherKey = other.name || other.label;
                              if (otherKey) delete next[otherKey];
                            });
                            return next;
                          })}
                          options={fieldOptions.map((option) => ({
                            value: option.value,
                            label: catText(option.label)
                          }))}
                          placeholder={t("Выберите")}
                          emptyText={t("Нет вариантов")}
                          ariaLabel={catText(field.label)}
                        />
                      ) : (
                        /* Числа и даты Kleinanzeigen не предлагает списком —
                           пробег, площадь, год выпуска вводятся руками. Раньше
                           для них рисовался тот же выпадающий список, но пустой:
                           обязательное поле, которое нечем заполнить, наглухо
                           перекрывало публикацию в Авто и Недвижимости.
                           Месяц даёт ровно тот формат, что у KL в объявлениях
                           (ГГГГ-ММ). */
                        <input
                          type={fieldInputType}
                          value={value}
                          inputMode={fieldInputType === "number" ? "numeric" : undefined}
                          step={String(field.type).toUpperCase() === "DECIMAL" ? "any" : undefined}
                          min={fieldInputType === "number" ? "0" : undefined}
                          placeholder={field.unit ? tf`в ${field.unit}` : t("Введите значение")}
                          aria-label={catText(field.label)}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            setExtraFieldValues((prev) => ({ ...prev, [key]: nextValue }));
                          }}
                          className="adf-input"
                        />
                      )}
                    </div>
                  );
                })}
                    </div>
                  ) : (
                    <div className={`nf-hint${newAd.accountId ? "" : " is-warn"}`}>
                      {newAd.accountId
                        ? t("Для выбранной категории нет дополнительных параметров.")
                        : t("Выберите аккаунт, чтобы загрузить параметры категории.")}
                    </div>
                  )}
                </div>
              )}

              {(() => {
                const selectedType = String(selectedAccountMeta?.accountType || "").trim().toLowerCase();
                // Тип мог быть записан и как «COMMERCIAL» — это тот же gewerblich, и
                // поле Impressum ему нужно так же (без него KL отклоняет объявление).
                if (selectedType !== "gewerblich" && selectedType !== "commercial") return null;
                return (
                  <div className="nf-card nf-params">
                    <div className="nf-params-head"><span>{t("Юридическая информация")}</span></div>
                    <textarea
                      className="adf-input"
                      value={newAd.legalInfo || ""}
                      onChange={(e) => setNewAd((prev) => ({ ...prev, legalInfo: e.target.value }))}
                      placeholder="Impressum / Rechtliche Angaben..."
                      rows="4"
                      style={{ minHeight: "96px" }}
                    />
                  </div>
                );
              })()}
            </div>

            {/* Публикация: большая кнопка, под ней — черновик и предпросмотр.
                Что не заполнено, видно по полосе «Готово N из 6» в шапке. */}
            <div className="nf-card nf-publish">
              <button type="button" className="ui-btn-primary kl-press-send nf-main-btn" onClick={handlePublishClick} disabled={publishing} title={`${MOD_KEY_LABEL}+Enter`}>
                {publishing
                  ? (<><LoadingIcon size={16} /> {t("Публикация...")}</>)
                  : (<><span className="kl-btn-ico"><SendIcon size={16} /></span> {t("Опубликовать")}{!isPhoneLayout && <kbd className="nf-kbd">{IS_MAC ? "⌘↵" : "Ctrl ↵"}</kbd>}</>)}
              </button>
              <div className="nf-sub-btns">
                <button type="button" onClick={onSaveDraft} disabled={publishing} title={`${MOD_KEY_LABEL}+S`}><FileIcon size={14} /> {t("Сохранить черновик")}</button>
                <button type="button" onClick={() => setShowPreview(true)}><EyeIcon size={14} /> {t("Предпросмотр")}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {photoEditorItem && (() => {
        const editedFile = photoEditorItem.source === "local"
          ? (Array.isArray(adImages) ? adImages[photoEditorItem.localIndex] : null)
          : null;
        return (
          <PhotoEditor
            key={photoEditorItem.key}
            source={editedFile || photoEditorItem.url}
            fileName={editedFile?.name || `photo-${(photoEditorItem.draftIndex ?? 0) + 1}.jpg`}
            onCancel={() => setPhotoEditorItem(null)}
            onSave={(file) => {
              replaceEditedPhoto(photoEditorItem, file);
              setPhotoEditorItem(null);
            }}
          />
        );
      })()}

      {previewPresence.mounted && (
        <AdPreviewDialog
          presenceState={previewPresence.state}
          isPhone={isPhoneLayout}
          photos={combinedImageItems}
          price={newAd.price}
          priceType={newAd.priceType}
          title={newAd.title}
          categoryPath={previewCategoryPath}
          postalCode={newAd.postalCode}
          city={plzPlace.code === String(newAd.postalCode || "").trim() ? plzPlace.city : ""}
          accountName={selectedAccountMeta ? previewAccountName : ""}
          accountEmail={previewAccountEmail}
          scheduledAt={newAd.scheduledAt}
          scheduledLabel={newAd.scheduledAt ? new Date(newAd.scheduledAt).toLocaleString(locale(), { timeZone: "Europe/Berlin" }) : ""}
          description={newAd.description}
          params={filledPreviewExtraFields}
          missingLabel={notReadyToPublish ? missingRequiredLabel : ""}
          publishing={publishing}
          onClose={() => setShowPreview(false)}
          onSaveDraft={onSaveDraft}
          onPublish={() => {
            // Как кнопка формы: недостающие параметры подсвечиваются в форме,
            // поэтому предпросмотр сначала закрывается.
            setShowPreview(false);
            handlePublishClick();
          }}
        />
      )}
    </div>
  );
};

export default AdModal;
